import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Step, StepLabel, Stepper, Switch, TextField, Typography, ListItemText, OutlinedInput, Grid, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DateInputWithArrows from '../components/DateInputWithArrows';
import DailyWorkMonthCalendar from '../components/DailyWorkMonthCalendar';
import AddTaskDialog from '../components/AddTaskDialog';

// Cross-browser compatible UUID generator
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

import {
  getProjects,
  getProjectGeneralTasks,
  getGeneralTasks,
  ensureProjectGeneralTask,
  getAvailablePersonnel,
  getCompanies,
  createPersonnelHours,
  bulkCreateProjectLaborEntries,
  getPersonnelHoursForProject,
  getProjectRecentPersonnelHours,
  updatePersonnelHours,
  deletePersonnelHours,
  getProjectLaborEntries,
  getProjectRecentLaborEntries,
  updateProjectLaborEntry,
  deleteProjectLaborEntry,
  getProjectFloorplans,
  getProjectFloorplanFileContent,
  getProjectTaskWorkInputs,
  getProjectTaskWorkInputsByRange,
  upsertProjectTaskWorkInput,
  upsertProjectTaskWorkInputNotes,
  deleteProjectTaskWorkInput,
  getProjectTaskWorkInputPhotoUploadUrl,
  createProjectTaskWorkInputPhoto,
  deleteProjectTaskWorkInputPhoto,
  getDailyWorkPreferences,
  getSiteDeliveriesForProject,
  getSiteDeparturesForProject,
} from '../services/api';
import {
  formatDateEuropean,
  toLocalDateString,
  rangeStringsForViewMonth,
} from '../utils/dateUtils';
import { hoursBetween, previewNetHours, isValidTimeHhMm } from '../utils/dailyWorkHours';
import { formatDailyWorkTaskLabel } from '../utils/generalTasks';

/** Local calendar keys; includes step2-only days (labor without personnel hours for that day). */
function buildMonthDateEntryStatus(hoursRows, laborRows, taskWorkInputRows, fromStr, toStr) {
  const datesWithStep1 = new Set((hoursRows || []).map((r) => toLocalDateString(r.date)).filter(Boolean));
  const datesWithStep2 = new Set();
  const step2TaskIdsByDate = new Map();
  const addStep2Task = (row) => {
    const dayStr = toLocalDateString(row.date);
    if (!dayStr || dayStr < fromStr || dayStr > toStr) return;
    if (!Number.isFinite(row.projectGeneralTaskId)) return;
    datesWithStep2.add(dayStr);
    if (!step2TaskIdsByDate.has(dayStr)) step2TaskIdsByDate.set(dayStr, new Set());
    step2TaskIdsByDate.get(dayStr).add(Number(row.projectGeneralTaskId));
  };
  (laborRows || []).forEach(addStep2Task);
  (taskWorkInputRows || []).forEach(addStep2Task);
  const step3TaskIdsByDate = new Map();
  (taskWorkInputRows || []).forEach((row) => {
    const dayStr = toLocalDateString(row.date);
    if (!dayStr || dayStr < fromStr || dayStr > toStr) return;
    if (!Number.isFinite(row.projectGeneralTaskId)) return;
    // Notes-only rows (no floorplan/pins) are Step 2 metadata, not Step 3 complete
    const hasPins = Boolean(
      row.floorplanObjectKey ||
      (Array.isArray(row.pinGroups) && row.pinGroups.length > 0) ||
      (Array.isArray(row.pins) && row.pins.length > 0) ||
      (row.pinXPercent != null && row.pinYPercent != null && row.floorplanObjectId != null)
    );
    if (!hasPins) return;
    if (!step3TaskIdsByDate.has(dayStr)) step3TaskIdsByDate.set(dayStr, new Set());
    step3TaskIdsByDate.get(dayStr).add(Number(row.projectGeneralTaskId));
  });
  const datesWithStep3Complete = new Set();
  step2TaskIdsByDate.forEach((step2TaskIds, dayStr) => {
    if (!step2TaskIds || step2TaskIds.size === 0) return;
    const step3TaskIds = step3TaskIdsByDate.get(dayStr) || new Set();
    let complete = true;
    step2TaskIds.forEach((taskId) => {
      if (!step3TaskIds.has(taskId)) complete = false;
    });
    if (complete) datesWithStep3Complete.add(dayStr);
  });
  const status = {};
  datesWithStep1.forEach((dayStr) => {
    if (datesWithStep2.has(dayStr)) {
      status[dayStr] = datesWithStep3Complete.has(dayStr) ? 'all' : 'step12';
    } else {
      status[dayStr] = 'step1';
    }
  });
  datesWithStep2.forEach((dayStr) => {
    if (status[dayStr] === undefined) {
      status[dayStr] = datesWithStep3Complete.has(dayStr) ? 'all' : 'step12';
    }
  });
  return status;
}
import { formatPersonnelName } from '../utils/personnelDisplay';
import { getApiErrorMessage } from '../utils/apiErrors';
import { UNITS } from '../constants/units';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TYPE_OPTIONS = [
  { value: 'Material', label: 'Material' },
  { value: 'Machine', label: 'Machine' },
  { value: 'Equipment', label: 'Equipment' },
];

const PIN_MARKER_COLORS = [
  '#d32f2f',
  '#1976d2',
  '#ed6c02',
  '#2e7d32',
  '#9c27b0',
  '#00838f',
  '#5d4037',
  '#455a64',
];

function getPinMarkerColor(pinIndex) {
  return PIN_MARKER_COLORS[pinIndex % PIN_MARKER_COLORS.length];
}

function PinNumberBadge({ pinIndex, size = 22 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: '50%',
        bgcolor: getPinMarkerColor(pinIndex),
        border: '2px solid white',
        boxShadow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-hidden
    >
      <Typography
        component="span"
        sx={{ color: 'common.white', fontSize: size <= 22 ? 11 : 13, fontWeight: 700, lineHeight: 1 }}
      >
        {pinIndex + 1}
      </Typography>
    </Box>
  );
}

function FloorplanPinPicker({ projectId, fileId, mimeType, pins, onPinsChange }) {
  const { t } = useTranslation();
  const measureRef = useRef(null);
  const [pageRenderWidth, setPageRenderWidth] = useState(560);
  const [zoom, setZoom] = useState(1);
  const [fileSource, setFileSource] = useState(null);
  const [loadingFile, setLoadingFile] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [renderError, setRenderError] = useState(null);
  const isImage = String(mimeType || '').startsWith('image/');

  useEffect(() => {
    if (!projectId || !fileId) {
      setFileSource(null);
      setLoadingFile(false);
      setLoadError(null);
      return undefined;
    }

    let objectUrl = null;
    let cancelled = false;
    setLoadingFile(true);
    setLoadError(null);
    setRenderError(null);

    getProjectFloorplanFileContent(projectId, fileId)
      .then(async (res) => {
        if (cancelled) return;
        const contentType = String(res.headers['content-type'] || '').toLowerCase();
        if (contentType.includes('text/html')) {
          throw new Error(
            'Server returned HTML instead of a PDF. The floorplan API route may be missing — redeploy with the latest server code.',
          );
        }
        if (contentType.includes('application/json')) {
          const text = await res.data.text();
          let message = 'Failed to load floorplan';
          try {
            const parsed = JSON.parse(text);
            message = parsed.error || message;
          } catch {
            message = text || message;
          }
          throw new Error(message);
        }
        const buffer = await res.data.arrayBuffer();
        if (!buffer || buffer.byteLength === 0) {
          throw new Error('Floorplan file is empty');
        }
        const blobType = isImage
          ? (mimeType || contentType || 'image/*')
          : (mimeType || contentType || 'application/pdf');
        const blob = new Blob([buffer], { type: blobType });
        objectUrl = URL.createObjectURL(blob);
        setFileSource(isImage ? { kind: 'image', url: objectUrl } : { kind: 'pdf', url: objectUrl });
      })
      .catch(async (err) => {
        if (cancelled) return;
        setFileSource(null);
        setLoadError(err?.message || await getApiErrorMessage(err, 'Failed to load floorplan'));
      })
      .finally(() => {
        if (!cancelled) setLoadingFile(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [projectId, fileId, isImage, mimeType]);

  useEffect(() => {
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (!w) return;
      const next = Math.max(200, Math.min(560, Math.floor(w)));
      setPageRenderWidth((prev) => (prev === next ? prev : next));
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const displayWidth = Math.round(pageRenderWidth * zoom);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('dailyWorkInput.step3.clickFloorplanToPlacePin', { defaultValue: 'Click on the floorplan to place a pin' })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={t('dailyWorkInput.step3.zoomOut', { defaultValue: 'Zoom out' })}>
            <span>
              <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} disabled={zoom <= 0.5}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title={t('dailyWorkInput.step3.zoomIn', { defaultValue: 'Zoom in' })}>
            <span>
              <IconButton size="small" onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))} disabled={zoom >= 3}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => setZoom(1)} disabled={zoom === 1}>
            {t('dailyWorkInput.step3.resetZoom', { defaultValue: 'Reset' })}
          </Button>
        </Box>
      </Box>
      <Box
        ref={measureRef}
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block', width: displayWidth, maxWidth: 'none' }}>
          {loadingFile && <CircularProgress size={20} />}
          {!loadingFile && (loadError || renderError) && (
            <Typography variant="body2" color="error">{loadError || renderError}</Typography>
          )}
          {!loadingFile && !loadError && !renderError && fileSource?.kind === 'image' && (
            <Box
              component="img"
              src={fileSource.url}
              alt=""
              sx={{ display: 'block', width: displayWidth, maxWidth: 'none', height: 'auto' }}
            />
          )}
          {!loadingFile && !loadError && !renderError && fileSource?.kind === 'pdf' && (
            <Document
              file={fileSource.url}
              loading={<CircularProgress size={20} />}
              onLoadError={(err) => setRenderError(err?.message || 'Failed to render PDF')}
            >
              <Page pageNumber={1} width={displayWidth} renderAnnotationLayer={false} renderTextLayer={false} />
            </Document>
          )}
          {!loadingFile && !loadError && !renderError && fileSource && (
            <Box
              onClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                const xPercent = ((event.clientX - rect.left) / rect.width) * 100;
                const yPercent = ((event.clientY - rect.top) / rect.height) * 100;
                onPinsChange([...(pins || []), { xPercent, yPercent }]);
              }}
              sx={{ position: 'absolute', inset: 0, cursor: 'crosshair' }}
            >
              {(pins || []).map((pin, idx) => (
                <Box
                  key={pin.pinKey || `${idx}-${pin.xPercent}-${pin.yPercent}`}
                  sx={{
                    position: 'absolute',
                    left: `${pin.xPercent}%`,
                    top: `${pin.yPercent}%`,
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }}
                >
                  <PinNumberBadge pinIndex={idx} size={24} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function DailyWorkInput() {
  const { t } = useTranslation();
  const steps = [
    t('dailyWorkInput.steps.generalInfoWorkers', { defaultValue: 'General info and workers' }),
    t('dailyWorkInput.steps.tasksPeopleMaterials', { defaultValue: 'Tasks, people and materials' }),
    t('dailyWorkInput.steps.locationPinPhotos', { defaultValue: 'Location pin and photos' }),
  ];

  // Number inputs change value on mouse wheel when focused; block that accidental change.
  useEffect(() => {
    const preventScrollValueChange = (event) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.type === 'number' &&
        document.activeElement === target
      ) {
        event.preventDefault();
      }
    };
    document.addEventListener('wheel', preventScrollValueChange, { passive: false });
    return () => document.removeEventListener('wheel', preventScrollValueChange);
  }, []);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectGeneralTasks, setProjectGeneralTasks] = useState([]);
  const [catalogTasks, setCatalogTasks] = useState([]);
  const [addingCatalogTask, setAddingCatalogTask] = useState(false);
  const [personnel, setPersonnel] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [existingHours, setExistingHours] = useState([]);
  const [editingExisting, setEditingExisting] = useState({}); // {id: { hours, startTime, endTime, usedLunch, notes }}
  const [editingLaborManDays, setEditingLaborManDays] = useState({}); // { laborEntryId: manDays string }
  const [dailyWorkPrefs, setDailyWorkPrefs] = useState({
    lunchDurationMinutes: 30,
    lunchIsPartOfWorkHours: false,
    dailyWorkHoursFormat: 'HOURS',
    defaultWorkStartTime: '08:00',
    defaultWorkEndTime: '16:00',
    defaultUsedLunch: false,
  });
  const [bulkStartTime, setBulkStartTime] = useState('08:00');
  const [bulkEndTime, setBulkEndTime] = useState('16:00');
  const [rainLunchDialogOpen, setRainLunchDialogOpen] = useState(false);
  const [workerNoteOpen, setWorkerNoteOpen] = useState({}); // { [eh-id|worker-key]: true }
  const [taskNotes, setTaskNotes] = useState({}); // { taskId: string }
  const [existingWorkInputTaskIds, setExistingWorkInputTaskIds] = useState([]);

  const unitOptions = useMemo(() => Object.values(UNITS), []);

  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState(() => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [workers, setWorkers] = useState([]);
  const [wasRaining, setWasRaining] = useState(false);

  const handleSelectDate = (dateStr) => {
    setDate(dateStr);
    setViewMonth(dateStr.slice(0, 7));
    setActiveStep(0);
  };
  const handleViewMonthChange = (delta) => {
    setViewMonth((prev) => {
      const [y, m] = prev.split('-').map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  };

  // workers state item shape:
  // { key, type: 'person'|'subcontractor', personnelId?, companyId?, subcontractorOrder?, hours, startTime, endTime, usedLunch, notes }

  const isTimeRangeFormat = dailyWorkPrefs.dailyWorkHoursFormat === 'TIME_RANGE';

  const getExistingDraft = (eh) => {
    const draft = editingExisting[eh.id] || {};
    const storedGross = Number(eh.hours) + (Number(eh.workhoursReducedForLunch) || 0);
    return {
      hours: draft.hours != null ? draft.hours : storedGross,
      startTime: draft.startTime != null ? draft.startTime : (eh.startTime || ''),
      endTime: draft.endTime != null ? draft.endTime : (eh.endTime || ''),
      usedLunch: draft.usedLunch != null ? draft.usedLunch : Boolean(eh.usedLunch),
      notes: draft.notes != null ? draft.notes : (eh.notes || ''),
    };
  };

  const patchExistingDraft = (id, patch) => {
    setEditingExisting((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  };

  const workerNetHours = (w) => {
    const preview = previewNetHours(w, dailyWorkPrefs);
    return preview ? preview.hours : 0;
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [projRes, compRes, prefsRes] = await Promise.all([
          getProjects(),
          getCompanies(),
          getDailyWorkPreferences().catch(() => ({ data: null })),
        ]);
        setProjects(projRes.data || []);
        setCompanies(compRes.data || []);
        if (prefsRes.data) {
          const nextPrefs = {
            lunchDurationMinutes: prefsRes.data.lunchDurationMinutes ?? 30,
            lunchIsPartOfWorkHours: Boolean(prefsRes.data.lunchIsPartOfWorkHours),
            dailyWorkHoursFormat: prefsRes.data.dailyWorkHoursFormat === 'TIME_RANGE' ? 'TIME_RANGE' : 'HOURS',
            defaultWorkStartTime: prefsRes.data.defaultWorkStartTime || '08:00',
            defaultWorkEndTime: prefsRes.data.defaultWorkEndTime || '16:00',
            defaultUsedLunch: Boolean(prefsRes.data.defaultUsedLunch),
          };
          setDailyWorkPrefs(nextPrefs);
          setBulkStartTime(nextPrefs.defaultWorkStartTime);
          setBulkEndTime(nextPrefs.defaultWorkEndTime);
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    async function loadCatalogTasks() {
      try {
        const catalogRes = await getGeneralTasks();
        setCatalogTasks(catalogRes.data || []);
      } catch (e) {
        setCatalogTasks([]);
      }
    }
    loadCatalogTasks();
  }, []);

  useEffect(() => {
    async function loadGeneralTasks() {
      if (!projectId) {
        setProjectGeneralTasks([]);
        return;
      }
      try {
        const projectRes = await getProjectGeneralTasks(projectId);
        setProjectGeneralTasks(projectRes.data?.tasks || []);
      } catch (e) {
        setProjectGeneralTasks([]);
      }
    }
    loadGeneralTasks();
  }, [projectId]);

  useEffect(() => {
    async function loadProjectMaterials() {
      if (!projectId) {
        setMaterials([]);
        return;
      }
      try {
        const [delRes, depRes] = await Promise.all([
          getSiteDeliveriesForProject(projectId),
          getSiteDeparturesForProject(projectId),
        ]);
        const deliveries = delRes.data || [];
        const departuresList = depRes.data || [];
        const deliveredMap = new Map();
        deliveries.forEach((row) => {
          const id = Number(row.materialEquipmentId);
          if (!Number.isFinite(id)) return;
          deliveredMap.set(id, (deliveredMap.get(id) || 0) + Number(row.quantity || 0));
        });
        const departedMap = new Map();
        departuresList.forEach((row) => {
          const id = Number(row.materialEquipmentId || row.materialId);
          if (!Number.isFinite(id)) return;
          departedMap.set(id, (departedMap.get(id) || 0) + Number(row.quantity || 0));
        });
        const items = [];
        deliveredMap.forEach((qty, id) => {
          const net = qty - (departedMap.get(id) || 0);
          if (net <= 0) return;
          const delivery = deliveries.find((row) => Number(row.materialEquipmentId) === id);
          items.push({
            id,
            name: delivery?.materialEquipment?.name || `#${id}`,
            quantity: net,
          });
        });
        items.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        setMaterials(items);
      } catch (e) {
        console.error(e);
        setMaterials([]);
      }
    }
    loadProjectMaterials();
  }, [projectId]);

  useEffect(() => {
    async function loadFloorplans() {
      if (!projectId) {
        setFloorplanObjects([]);
        return;
      }
      try {
        const res = await getProjectFloorplans(projectId);
        setFloorplanObjects(res.data || []);
      } catch (e) {
        console.error(e);
        setFloorplanObjects([]);
      }
    }
    loadFloorplans();
  }, [projectId]);

  const loadStep3Inputs = useCallback(async () => {
    if (!projectId || !date) {
      setTaskWorkStep3({});
      return;
    }
    try {
      const res = await getProjectTaskWorkInputs(projectId, date);
      const map = {};
      (res.data || []).forEach((row) => {
        const hasPinData = Boolean(
          (Array.isArray(row.pinGroups) && row.pinGroups.length > 0) ||
          (Array.isArray(row.pins) && row.pins.length > 0) ||
          row.floorplanObjectKey
        );
        let pinGroups = [];
        if (Array.isArray(row.pinGroups) && row.pinGroups.length > 0) {
          pinGroups = row.pinGroups;
        } else if (hasPinData) {
          pinGroups = [{
            groupKey: generateUUID(),
            floorplanObjectId: row.floorplanObjectId,
            floorplanFloorNumber: row.floorplanFloorNumber,
            floorplanFileId: row.floorplanFileId,
            floorplanObjectKey: row.floorplanObjectKey,
            floorplanOriginalName: row.floorplanOriginalName,
            floorplanMimeType: row.floorplanMimeType,
            pins: (Array.isArray(row.pins) && row.pins.length > 0
              ? row.pins
              : [{ xPercent: row.pinXPercent, yPercent: row.pinYPercent }]).map((pin) => ({
                pinKey: pin.pinKey || generateUUID(),
                xPercent: pin.xPercent,
                yPercent: pin.yPercent,
              })),
          }];
        }
        map[row.projectGeneralTaskId] = {
          pinGroups,
          photos: row.photos || [],
          notes: row.notes || '',
        };
      });
      setTaskWorkStep3(map);
    } catch (e) {
      console.error(e);
      setTaskWorkStep3({});
    }
  }, [projectId, date]);

  useEffect(() => {
    loadStep3Inputs();
  }, [loadStep3Inputs]);

  useEffect(() => {
    async function loadPersonnel() {
      if (!date) return setPersonnel([]);
      try {
        const res = await getAvailablePersonnel(date);
        setPersonnel(res.data || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
    loadPersonnel();
  }, [date]);

  // Reset unsaved changes when date changes
  useEffect(() => {
    // Clear any unsaved local state when switching dates
    setWorkers([]);
    setTaskWork({});
    setTaskWorkStep3({});
    setEditingExisting({});
    setEditingLaborManDays({});
    setWasRaining(false);
    setTaskNotes({});
    setExistingWorkInputTaskIds([]);
  }, [date]);

  useEffect(() => {
    if (!existingHours.length) {
      setWasRaining(false);
      return;
    }
    setWasRaining(existingHours.some((entry) => Boolean(entry.wasRaining)));
  }, [existingHours]);

  useEffect(() => {
    async function loadExistingHours() {
      if (!projectId || !date) { setExistingHours([]); return; }
      try {
        const res = await getPersonnelHoursForProject(projectId, { from: date, to: date });
        setExistingHours(res.data || []);
        setEditingExisting({});
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
    loadExistingHours();
  }, [projectId, date]);

  useEffect(() => {
    async function loadTaskNotes() {
      if (!projectId || !date) {
        setTaskNotes({});
        setExistingWorkInputTaskIds([]);
        return;
      }
      try {
        const res = await getProjectTaskWorkInputs(projectId, date);
        const map = {};
        const ids = [];
        (res.data || []).forEach((row) => {
          ids.push(Number(row.projectGeneralTaskId));
          if (row.notes) map[row.projectGeneralTaskId] = row.notes;
        });
        setTaskNotes(map);
        setExistingWorkInputTaskIds(ids);
      } catch (e) {
        console.error(e);
        setTaskNotes({});
        setExistingWorkInputTaskIds([]);
      }
    }
    loadTaskNotes();
  }, [projectId, date]);

  useEffect(() => {
    async function loadExistingLabor() {
      if (!projectId || !date) { setExistingLabor([]); return; }
      try {
        const res = await getProjectLaborEntries(projectId);
        const filtered = (res.data || []).filter((le) => toLocalDateString(le.date) === date);
        setExistingLabor(filtered);
        setEditingLaborManDays({});
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
    loadExistingLabor();
  }, [projectId, date]);

  const getLaborManDaysDisplay = (le) => (
    editingLaborManDays[le.id] != null ? editingLaborManDays[le.id] : String(le.hoursWorked / 8)
  );

  const getEffectiveLaborHoursWorked = (le) => {
    if (editingLaborManDays[le.id] != null) {
      if (editingLaborManDays[le.id] === '') return 0;
      const manDays = parseFloat(editingLaborManDays[le.id]);
      if (!Number.isFinite(manDays)) return le.hoursWorked;
      return manDays * 8;
    }
    return le.hoursWorked;
  };

  // Load which dates have step 1 only vs both steps for the displayed calendar month (for red/green dots)
  useEffect(() => {
    async function loadDateEntryStatus() {
      await refreshCalendarMonthStatus();
    }
    loadDateEntryStatus();
  }, [projectId, viewMonth]);

  const handleAddPerson = () => {
    setWorkers((prev) => [...prev, {
      key: generateUUID(),
      type: 'person',
      personnelId: '',
      hours: '',
      startTime: '',
      endTime: '',
      usedLunch: Boolean(dailyWorkPrefs.defaultUsedLunch),
      notes: '',
    }]);
  };

  const handleAddSubcontractor = () => {
    setWorkers((prev) => [...prev, {
      key: generateUUID(),
      type: 'subcontractor',
      companyId: '',
      hours: '',
      startTime: '',
      endTime: '',
      usedLunch: Boolean(dailyWorkPrefs.defaultUsedLunch),
      notes: '',
    }]);
  };

  const openWorkerNote = (id) => {
    setWorkerNoteOpen((prev) => ({ ...prev, [id]: true }));
  };

  const isWorkerNoteVisible = (id, notes) => (
    Boolean(workerNoteOpen[id]) || Boolean(String(notes || '').trim())
  );

  const isTimeValueEmpty = (value) => value == null || String(value).trim() === '';

  const handleBulkSetFrom = () => {
    if (!isValidTimeHhMm(bulkStartTime)) return;
    setWorkers((prev) => prev.map((w) => (
      isTimeValueEmpty(w.startTime) ? { ...w, startTime: bulkStartTime } : w
    )));
    setEditingExisting((prev) => {
      const next = { ...prev };
      existingHours.forEach((eh) => {
        const current = next[eh.id]?.startTime != null ? next[eh.id].startTime : (eh.startTime || '');
        if (isTimeValueEmpty(current)) {
          next[eh.id] = { ...(next[eh.id] || {}), startTime: bulkStartTime };
        }
      });
      return next;
    });
  };

  const handleBulkSetTo = () => {
    if (!isValidTimeHhMm(bulkEndTime)) return;
    setWorkers((prev) => prev.map((w) => (
      isTimeValueEmpty(w.endTime) ? { ...w, endTime: bulkEndTime } : w
    )));
    setEditingExisting((prev) => {
      const next = { ...prev };
      existingHours.forEach((eh) => {
        const current = next[eh.id]?.endTime != null ? next[eh.id].endTime : (eh.endTime || '');
        if (isTimeValueEmpty(current)) {
          next[eh.id] = { ...(next[eh.id] || {}), endTime: bulkEndTime };
        }
      });
      return next;
    });
  };

  const clearLunchForAllWorkers = () => {
    setWorkers((prev) => prev.map((w) => ({ ...w, usedLunch: false })));
    setEditingExisting((prev) => {
      const next = { ...prev };
      existingHours.forEach((eh) => {
        next[eh.id] = { ...(next[eh.id] || {}), usedLunch: false };
      });
      return next;
    });
  };

  const handleRainDayToggle = (checked) => {
    if (checked) {
      setRainLunchDialogOpen(true);
      return;
    }
    setWasRaining(false);
  };

  const confirmRainDisableLunch = () => {
    setWasRaining(true);
    clearLunchForAllWorkers();
    setRainLunchDialogOpen(false);
  };

  const confirmRainKeepLunch = () => {
    setWasRaining(true);
    setRainLunchDialogOpen(false);
  };

  const cancelRainDialog = () => {
    setRainLunchDialogOpen(false);
  };

  const handleRemoveWorker = async (key) => {
    const worker = workers.find(w => w.key === key);
    if (!worker) return;
    
    // Remove from workers array
    setWorkers((prev) => prev.filter((w) => w.key !== key));
    
    // Also remove any existing labor entries for this person
    if (projectId && date) {
      try {
        const res = await getProjectLaborEntries(projectId);
        const existingEntries = (res.data || []).filter(le => 
          toLocalDateString(le.date) === date &&
          (
            (worker.type === 'person' && le.personnelId === Number(worker.personnelId)) ||
            (
              worker.type === 'subcontractor' &&
              le.subcontractorCompanyId === Number(worker.companyId) &&
              Number(le.subcontractorOrder) === Number(worker.subcontractorOrder)
            )
          )
        );
        
        // Delete all labor entries for this person
        for (const entry of existingEntries) {
          await deleteProjectLaborEntry(projectId, entry.id);
        }
        
        // Refresh existing labor entries
        const updatedRes = await getProjectLaborEntries(projectId);
        setExistingLabor((updatedRes.data || []).filter((x) => toLocalDateString(x.date) === date));
      } catch (e) {
        console.error('Error removing labor entries:', e);
      }
    }
    
    // Remove from taskWork state if any entries exist
    setTaskWork((prev) => {
      const updated = { ...prev };
      // Remove any entries for this worker from all cost sheets
      Object.keys(updated).forEach((taskId) => {
        if (updated[taskId]?.people) {
          updated[taskId].people = updated[taskId].people.filter((p) => p.key !== key);
        }
      });
      return updated;
    });
  };

  const totalHours = useMemo(() => {
    const newWorkersHours = workers.reduce((sum, w) => sum + workerNetHours(w), 0);
    const existingHoursTotal = existingHours.reduce((sum, eh) => {
      const draft = getExistingDraft(eh);
      const preview = previewNetHours(draft, dailyWorkPrefs);
      return sum + (preview ? preview.hours : (Number(eh.hours) || 0));
    }, 0);
    return Math.round((newWorkersHours + existingHoursTotal) * 1000) / 1000;
  }, [workers, existingHours, editingExisting, dailyWorkPrefs]);

  const [taskWork, setTaskWork] = useState({});
  const [existingLabor, setExistingLabor] = useState([]);
  const [floorplanObjects, setFloorplanObjects] = useState([]);
  const [taskWorkStep3, setTaskWorkStep3] = useState({});
  const [dateEntryStatus, setDateEntryStatus] = useState({}); // calendar month: step1 | step12 | all
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [recentEntries, setRecentEntries] = useState(null);
  const [duplicateStep, setDuplicateStep] = useState(null); // 'step1' or 'step2'
  // taskWork: { [taskId]: { people, materials } }

  const refreshCalendarMonthStatus = async () => {
    if (!projectId || !viewMonth) {
      setDateEntryStatus({});
      return;
    }
    const { fromStr, toStr } = rangeStringsForViewMonth(viewMonth);
    if (!fromStr || !toStr) {
      setDateEntryStatus({});
      return;
    }

    let hoursRows = [];
    let laborRows = [];
    let taskWorkInputRows = [];
    try {
      const hoursRes = await getPersonnelHoursForProject(projectId, { from: fromStr, to: toStr });
      hoursRows = hoursRes.data || [];
    } catch (e) {
      console.error(e);
    }
    try {
      const laborRes = await getProjectLaborEntries(projectId);
      laborRows = laborRes.data || [];
    } catch (e) {
      console.error(e);
    }
    try {
      const step3Res = await getProjectTaskWorkInputsByRange(projectId, fromStr, toStr);
      taskWorkInputRows = step3Res.data || [];
    } catch (e) {
      console.error(e);
    }

    setDateEntryStatus(buildMonthDateEntryStatus(hoursRows, laborRows, taskWorkInputRows, fromStr, toStr));
  };

  const isValidTimeRangeEntry = (startTime, endTime) => {
    if (!isValidTimeHhMm(startTime)) return false;
    const endRaw = endTime != null && String(endTime).trim() ? String(endTime).trim() : '';
    if (!endRaw) return true;
    const span = hoursBetween(startTime, endTime);
    return span != null && span > 0;
  };

  const canProceedStep1 = useMemo(() => {
    if (!projectId) return false;
    if (!date) return false;
    if (!workers.length && existingHours.length === 0) return false;
    const selectedPersonnel = new Set();
    for (const w of workers) {
      if (w.type === 'person') {
        if (!w.personnelId) return false;
        const personKey = String(w.personnelId);
        if (selectedPersonnel.has(personKey) || existingHours.some((eh) => String(eh.personnelId) === personKey)) return false;
        selectedPersonnel.add(personKey);
      }
      if (w.type === 'subcontractor') {
        if (!w.companyId) return false;
      }
      if (isTimeRangeFormat) {
        if (!isValidTimeRangeEntry(w.startTime, w.endTime)) return false;
      } else {
        const h = parseFloat(w.hours);
        if (!Number.isFinite(h) || h <= 0) return false;
      }
    }
    for (const eh of existingHours) {
      if (editingExisting[eh.id] == null) continue;
      const draft = getExistingDraft(eh);
      if (isTimeRangeFormat) {
        if (!isValidTimeRangeEntry(draft.startTime, draft.endTime)) return false;
      } else {
        const h = parseFloat(draft.hours);
        if (!Number.isFinite(h) || h < 0) return false;
      }
    }
    return true;
  }, [projectId, date, workers, existingHours, editingExisting, isTimeRangeFormat, dailyWorkPrefs]);

  const handleSaveStep1 = async () => {
    setLoading(true);
    try {
      const existingHourUpdates = existingHours
        .filter((eh) => editingExisting[eh.id] != null)
        .map((eh) => {
          const draft = getExistingDraft(eh);
          return {
            id: eh.id,
            hours: draft.hours,
            startTime: draft.startTime || null,
            endTime: draft.endTime || null,
            usedLunch: Boolean(draft.usedLunch),
            notes: draft.notes || null,
          };
        });

      if (existingHourUpdates.length > 0) {
        await Promise.all(
          existingHourUpdates.map((item) =>
            updatePersonnelHours(item.id, {
              hours: item.hours,
              startTime: item.startTime,
              endTime: item.endTime,
              usedLunch: item.usedLunch,
              notes: item.notes,
            })
          )
        );
      }

      const entries = workers.map((w) => ({
        personnelId: w.type === 'person' ? Number(w.personnelId) : undefined,
        subcontractorCompanyId: w.type === 'subcontractor' ? Number(w.companyId) : undefined,
        hours: w.hours !== '' ? Number(w.hours) : undefined,
        startTime: w.startTime || null,
        endTime: w.endTime || null,
        usedLunch: Boolean(w.usedLunch),
        notes: w.notes || null,
      }));
      if (entries.length > 0) {
        await createPersonnelHours({ projectId: Number(projectId), date, entries, wasRaining });
      } else if (existingHours.length > 0) {
        // Persist rain flag when only editing existing / rain without new rows
        await createPersonnelHours({ projectId: Number(projectId), date, entries: [], wasRaining });
      }
      
      // Clear the workers state after successful save
      setWorkers([]);
      
      // Refresh existing hours and calendar dots
      try {
        const res = await getPersonnelHoursForProject(projectId, { from: date, to: date });
        setExistingHours(res.data || []);
        setEditingExisting({});
        await refreshCalendarMonthStatus();
      } catch (refreshError) {
        console.error('Error refreshing existing hours:', refreshError);
      }

      setActiveStep(1);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateRecentStep1 = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const res = await getProjectRecentPersonnelHours(projectId);
      if (res.data && res.data.entries && res.data.entries.length > 0) {
        setRecentEntries(res.data);
        setDuplicateStep('step1');
        setDuplicateDialogOpen(true);
      } else {
        alert('No recent entries found for this project.');
      }
    } catch (e) {
      console.error('Error fetching recent entries:', e);
      alert('Error fetching recent entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateRecentStep2 = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const res = await getProjectRecentLaborEntries(projectId);
      if (res.data && res.data.entries && res.data.entries.length > 0) {
        setRecentEntries(res.data);
        setDuplicateStep('step2');
        setDuplicateDialogOpen(true);
      } else {
        alert('No recent entries found for this project.');
      }
    } catch (e) {
      console.error('Error fetching recent entries:', e);
      alert('Error fetching recent entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDuplicateStep1 = () => {
    if (!recentEntries) return;

    // Create separate entries for each personnelHours record (personnel and subcontractors)
    const duplicatedWorkers = recentEntries.entries
      .filter((entry) => {
        if (!(entry.personnelId || entry.subcontractorCompanyId)) return false;
        if (isTimeRangeFormat) {
          return Boolean(entry.startTime);
        }
        const hours = Number(entry.hours);
        return Number.isFinite(hours) && hours > 0;
      })
      .map((entry) => ({
        key: generateUUID(),
        type: entry.subcontractorCompanyId ? 'subcontractor' : 'person',
        personnelId: entry.personnelId || '',
        companyId: entry.subcontractorCompanyId || '',
        subcontractorOrder: entry.subcontractorOrder != null ? entry.subcontractorOrder : null,
        // Reconstruct gross hours so lunch reduction can be re-applied with current org settings
        hours: Number(entry.hours) + (Number(entry.workhoursReducedForLunch) || 0),
        startTime: entry.startTime || '',
        endTime: entry.endTime || '',
        usedLunch: Boolean(entry.usedLunch),
        notes: entry.notes || '',
      }));

    setWorkers(duplicatedWorkers);
    setWasRaining(recentEntries.entries.some((entry) => Boolean(entry.wasRaining)));
    
    setDuplicateDialogOpen(false);
    setRecentEntries(null);
    setDuplicateStep(null);
  };

  const handleApplyDuplicateStep2 = () => {
    if (!recentEntries) return;

    // Allow duplication only when current Step 1 workers are a superset
    // of the workers used in the most recent Step 2 input.
    const availableStep1WorkerKeys = new Set(getStep1People.map((p) => p.slotKey));
    const requiredRecentWorkerKeys = new Set();

    recentEntries.entries.forEach((entry) => {
      if (!(entry.hoursWorked > 0)) return;
      if (entry.personnelId) {
        requiredRecentWorkerKeys.add(`person-${entry.personnelId}`);
      } else if (entry.subcontractorCompanyId) {
        requiredRecentWorkerKeys.add(
          `subcontractor-${entry.subcontractorCompanyId}-${entry.subcontractorOrder != null ? entry.subcontractorOrder : ''}`
        );
      }
    });

    const missingRequiredWorkerKeys = Array.from(requiredRecentWorkerKeys).filter(
      (key) => !availableStep1WorkerKeys.has(key)
    );

    if (missingRequiredWorkerKeys.length > 0) {
      const workerLabelByKey = new Map(getStep1People.map((p) => {
        const label = p.type === 'person'
          ? formatPersonnelName(p)
          : `${p.name}${p.subcontractorOrder != null ? ` (#${p.subcontractorOrder})` : ''}`;
        return [p.slotKey, label];
      }));

      const missingLabels = missingRequiredWorkerKeys.map((key) => {
        if (workerLabelByKey.has(key)) return workerLabelByKey.get(key);
        if (key.startsWith('person-')) return `Person ID ${key.replace('person-', '')}`;
        const [, companyId, order = ''] = key.split('-');
        return `Subcontractor company ID ${companyId}${order ? ` (#${order})` : ''}`;
      });

      alert(
        `Cannot duplicate most recent step 2 input because some workers are missing in step 1: ${missingLabels.join(', ')}.`
      );
      return;
    }

    // Populate taskWork for step 2
    const taskWorkMap = {};
    
    recentEntries.entries.forEach((entry) => {
      if (entry.hoursWorked > 0 || entry.amount) {
        const taskId = entry.projectGeneralTaskId;
        if (!taskId) return;

        if (!taskWorkMap[taskId]) {
          taskWorkMap[taskId] = { people: [], materials: [] };
        }

        if (entry.hoursWorked > 0) {
          const personKey = generateUUID();
          taskWorkMap[taskId].people.push({
            key: personKey,
            type: entry.personnelId ? 'person' : 'subcontractor',
            personnelId: entry.personnelId || '',
            companyId: entry.subcontractorCompanyId || '',
            subcontractorOrder: entry.subcontractorOrder != null ? entry.subcontractorOrder : null,
            manDays: entry.hoursWorked / 8,
          });
        }

        if (entry.amount) {
          const materialKey = generateUUID();
          const materialEntry = {
            key: materialKey,
            amount: entry.amount,
          };

          if (entry.materialEquipmentId) {
            materialEntry.equipmentType = 'our';
            materialEntry.materialEquipmentId = entry.materialEquipmentId;
            materialEntry.subcontractorEquipmentName = '';
            materialEntry.subcontractorEquipmentType = '';
            materialEntry.subcontractorEquipmentUnit = '';
          } else if (entry.subcontractorEquipmentName) {
            materialEntry.equipmentType = 'subcontractor';
            materialEntry.materialEquipmentId = '';
            materialEntry.subcontractorEquipmentName = entry.subcontractorEquipmentName;
            materialEntry.subcontractorEquipmentType = entry.subcontractorEquipmentType || '';
            materialEntry.subcontractorEquipmentUnit = entry.subcontractorEquipmentUnit || '';
          }

          taskWorkMap[taskId].materials.push(materialEntry);
        }
      }
    });
    
    setTaskWork(taskWorkMap);
    setDuplicateDialogOpen(false);
    setRecentEntries(null);
    setDuplicateStep(null);
  };

  const addPersonToCostSheet = (costSheetId) => {
    if ((step2PeopleCountByTaskId.get(Number(costSheetId)) || 0) >= step1TotalPeopleCount) return;
    setTaskWork((prev) => {
      const current = prev[costSheetId] || { people: [], materials: [] };
      return {
        ...prev,
        [costSheetId]: {
          ...current,
          people: [...current.people, { key: generateUUID(), personnelId: '', companyId: '', subcontractorOrder: null, manDays: 0.5, type: 'person' }]
        }
      };
    });
  };

  const addSubcontractorToCostSheet = (costSheetId) => {
    if ((step2PeopleCountByTaskId.get(Number(costSheetId)) || 0) >= step1TotalPeopleCount) return;
    setTaskWork((prev) => {
      const current = prev[costSheetId] || { people: [], materials: [] };
      return {
        ...prev,
        [costSheetId]: {
          ...current,
          people: [...current.people, { key: generateUUID(), personnelId: '', companyId: '', subcontractorOrder: null, manDays: 0.5, type: 'subcontractor' }]
        }
      };
    });
  };

  const updatePersonInCostSheet = (costSheetId, personKey, patch) => {
    setTaskWork((prev) => {
      const current = prev[costSheetId] || { people: [], materials: [] };
      const people = current.people.map(p => p.key === personKey ? { ...p, ...patch } : p);
      return { ...prev, [costSheetId]: { ...current, people } };
    });
  };

  const removePersonFromCostSheet = (costSheetId, personKey) => {
    setTaskWork((prev) => {
      const current = prev[costSheetId] || { people: [], materials: [] };
      const people = current.people.filter(p => p.key !== personKey);
      return { ...prev, [costSheetId]: { ...current, people } };
    });
  };

  const addMaterialToCostSheet = (costSheetId) => {
    setTaskWork((prev) => {
      const current = prev[costSheetId] || { people: [], materials: [] };
      return {
        ...prev,
        [costSheetId]: {
          ...current,
          materials: [...current.materials, { 
            key: generateUUID(), 
            equipmentType: 'our', // Default to our equipment
            materialEquipmentId: '', 
            subcontractorEquipmentName: '',
            subcontractorEquipmentType: '',
            subcontractorEquipmentUnit: '',
            amount: '' 
          }]
        }
      };
    });
  };

  const updateMaterialInCostSheet = (costSheetId, materialKey, patch) => {
    setTaskWork((prev) => {
      const current = prev[costSheetId] || { people: [], materials: [] };
      const materials = current.materials.map(m => m.key === materialKey ? { ...m, ...patch } : m);
      return { ...prev, [costSheetId]: { ...current, materials } };
    });
  };

  const removeMaterialFromCostSheet = (costSheetId, materialKey) => {
    setTaskWork((prev) => {
      const current = prev[costSheetId] || { people: [], materials: [] };
      const materials = current.materials.filter(m => m.key !== materialKey);
      return { ...prev, [costSheetId]: { ...current, materials } };
    });
  };

  const removeTask = async (taskId) => {
    setTaskWork((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });
    setExistingWorkInputTaskIds((prev) => prev.filter((id) => Number(id) !== Number(taskId)));
    setTaskNotes((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    setTaskWorkStep3((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });

    if (projectId && date) {
      try {
        await deleteProjectTaskWorkInput(Number(projectId), taskId, date);
        const res = await getProjectLaborEntries(projectId);
        const entriesToDelete = (res.data || []).filter(
          (le) =>
            toLocalDateString(le.date) === date &&
            le.projectGeneralTaskId === taskId
        );

        for (const entry of entriesToDelete) {
          await deleteProjectLaborEntry(projectId, entry.id);
        }

        const updatedRes = await getProjectLaborEntries(projectId);
        setExistingLabor((updatedRes.data || []).filter((x) => toLocalDateString(x.date) === date));
        await refreshCalendarMonthStatus();
      } catch (e) {
        console.error('Error removing task labor entries:', e);
      }
    }
  };

  const saveStep2AndContinue = async () => {
    if (!canSubmitStep2) return;
    setLoading(true);
    try {
      const existingLaborUpdates = existingLabor
        .filter((le) => (le.personnelId || le.subcontractorCompanyId) && editingLaborManDays[le.id] != null)
        .map((le) => {
          const raw = editingLaborManDays[le.id];
          if (raw === '') return null;
          const manDays = parseFloat(raw);
          if (!Number.isFinite(manDays) || manDays <= 0) return null;
          return { id: le.id, hoursWorked: manDays * 8 };
        })
        .filter(Boolean);

      if (existingLaborUpdates.length > 0) {
        await Promise.all(
          existingLaborUpdates.map((item) =>
            updateProjectLaborEntry(projectId, item.id, { hoursWorked: item.hoursWorked })
          )
        );
      }

      const entries = [];
      
      for (const [taskIdStr, work] of Object.entries(taskWork)) {
        const projectGeneralTaskId = Number(taskIdStr);
        for (const person of work.people || []) {
          if (person.personnelId || person.companyId) {
            const hoursWorked = Number(person.manDays) * 8;
            entries.push({
              projectGeneralTaskId,
              personnelId: person.type === 'person' ? Number(person.personnelId) : undefined,
              subcontractorCompanyId: person.type === 'subcontractor' ? Number(person.companyId) : undefined,
              subcontractorOrder: person.type === 'subcontractor' ? Number(person.subcontractorOrder) : undefined,
              hoursWorked,
              date,
            });
          }
        }

        for (const material of work.materials || []) {
          if (material.amount) {
            const entry = {
              projectGeneralTaskId,
              amount: Number(material.amount),
              hoursWorked: 0,
              date,
            };
            
            if (material.equipmentType === 'our' && material.materialEquipmentId) {
              // Our equipment
              entry.materialEquipmentId = Number(material.materialEquipmentId);
            } else if (material.equipmentType === 'subcontractor' && material.subcontractorEquipmentName) {
              // Subcontractor equipment
              entry.subcontractorEquipmentName = material.subcontractorEquipmentName;
              entry.subcontractorEquipmentType = material.subcontractorEquipmentType || '';
              entry.subcontractorEquipmentUnit = material.subcontractorEquipmentUnit || '';
            }
            
            entries.push(entry);
          }
        }
      }
      
      const filtered = entries.filter(
        (e) => Number.isFinite(e.projectGeneralTaskId) && (Number.isFinite(e.hoursWorked) || e.amount != null)
      );
      
      // Only submit if there are new entries to create
      if (filtered.length > 0) {
        await bulkCreateProjectLaborEntries(Number(projectId), filtered);
      }

      // Persist optional per-task notes for this date. Empty editor is allowed:
      // leftover DailyTaskWorkInput rows are deleted so the day stays empty.
      const usedTasks = getUsedTasks;
      const usedIdSet = new Set(usedTasks.map((task) => Number(task.id)));
      await Promise.all(
        usedTasks.map((task) =>
          upsertProjectTaskWorkInputNotes(Number(projectId), task.id, {
            date,
            notes: taskNotes[task.id] || null,
            ensure: true,
          })
        )
      );
      try {
        const existingInputsRes = await getProjectTaskWorkInputs(projectId, date);
        const leftover = (existingInputsRes.data || []).filter(
          (row) => !usedIdSet.has(Number(row.projectGeneralTaskId))
        );
        if (leftover.length > 0) {
          await Promise.all(
            leftover.map((row) =>
              deleteProjectTaskWorkInput(projectId, row.projectGeneralTaskId, date)
            )
          );
        }
      } catch (e) {
        console.error('Error removing leftover task work inputs:', e);
      }
      setExistingWorkInputTaskIds(usedTasks.map((task) => task.id));
      setTaskWorkStep3((prev) => {
        const next = {};
        usedTasks.forEach((task) => {
          const note = taskNotes[task.id] || '';
          next[task.id] = {
            ...(prev[task.id] || { photos: [], pinGroups: [], notes: '' }),
            notes: note,
          };
        });
        return next;
      });
      
      // Refresh existing data and calendar dots
      if (projectId && date) {
        try {
          setEditingExisting({});
          setEditingLaborManDays({});
          try {
            const hoursRes = await getPersonnelHoursForProject(projectId, { from: date, to: date });
            setExistingHours(hoursRes.data || []);
          } catch (e) {
            console.error(e);
          }
          let laborRows = [];
          try {
            const laborRes = await getProjectLaborEntries(projectId);
            laborRows = laborRes.data || [];
            setExistingLabor(laborRows.filter((le) => toLocalDateString(le.date) === date));
          } catch (e) {
            console.error(e);
          }
          await refreshCalendarMonthStatus();
        } catch (refreshError) {
          console.error('Error refreshing data after save:', refreshError);
        }
      }

      // Step 2 entries are persisted at this point; clear local draft rows
      // so going back from step 3 shows only fresh backend state.
      setTaskWork({});

      // Continue to step 3 for floorplan pin + photos
      setActiveStep(2);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStep2 = async () => {
    if (projectId && date) {
      try {
        const laborRes = await getProjectLaborEntries(projectId);
        const laborRows = laborRes.data || [];
        setExistingLabor(laborRows.filter((le) => toLocalDateString(le.date) === date));
        setEditingLaborManDays({});
      } catch (e) {
        console.error(e);
      }
    }
    setTaskWork({});
    setActiveStep(1);
  };

  const updateTaskStep3 = (taskId, patch) => {
    setTaskWorkStep3((prev) => ({
      ...prev,
      [taskId]: {
        ...(prev[taskId] || { photos: [], pinGroups: [], notes: '' }),
        ...patch,
      },
    }));
  };

  const handleSelectTaskFloorplan = (taskId, groupKey, floorplanObjectId, floorNumber) => {
    const objectRow = floorplanObjects.find((obj) => obj.id === Number(floorplanObjectId));
    const file = (objectRow?.files || []).find((f) => f.floorNumber === Number(floorNumber));
    setTaskWorkStep3((prev) => {
      const current = prev[taskId] || { photos: [], pinGroups: [], notes: '' };
      const nextGroups = (current.pinGroups || []).map((group) => (
        group.groupKey === groupKey
          ? {
            ...group,
            floorplanObjectId: Number(floorplanObjectId),
            floorplanFloorNumber: Number(floorNumber),
            floorplanFileId: file?.id || null,
            floorplanObjectKey: file?.objectKey || '',
            floorplanOriginalName: file?.originalName || '',
            floorplanMimeType: file?.mimeType || '',
            pins: [],
          }
          : group
      ));
      return { ...prev, [taskId]: { ...current, pinGroups: nextGroups } };
    });
  };

  const addPinGroup = (taskId) => {
    setTaskWorkStep3((prev) => {
      const current = prev[taskId] || { photos: [], pinGroups: [], notes: '' };
      return {
        ...prev,
        [taskId]: {
          ...current,
          pinGroups: [
            ...(current.pinGroups || []),
            {
              groupKey: generateUUID(),
              floorplanObjectId: '',
              floorplanFloorNumber: '',
              floorplanFileId: null,
              floorplanObjectKey: '',
              floorplanOriginalName: '',
              floorplanMimeType: '',
              pins: [],
            },
          ],
        },
      };
    });
  };

  const removePinGroup = async (taskId, groupKey) => {
    const current = taskWorkStep3[taskId] || { photos: [], pinGroups: [], notes: '' };
    const removedPinKeys = new Set(
      (current.pinGroups || [])
        .filter((g) => g.groupKey === groupKey)
        .flatMap((g) => (g.pins || []).map((p) => p.pinKey).filter(Boolean))
    );
    const photosToDelete = (current.photos || []).filter(
      (photo) => photo.id && removedPinKeys.has(photo.pinKey)
    );
    setTaskWorkStep3((prev) => {
      const latest = prev[taskId] || { photos: [], pinGroups: [], notes: '' };
      return {
        ...prev,
        [taskId]: {
          ...latest,
          pinGroups: (latest.pinGroups || []).filter((g) => g.groupKey !== groupKey),
          photos: (latest.photos || []).filter((photo) => !removedPinKeys.has(photo.pinKey)),
        },
      };
    });
    if (projectId && photosToDelete.length > 0) {
      await Promise.all(
        photosToDelete.map((photo) =>
          deleteProjectTaskWorkInputPhoto(projectId, photo.id).catch((e) => console.error(e))
        )
      );
    }
  };

  const removePin = async (taskId, groupKey, pinIndex) => {
    const current = taskWorkStep3[taskId] || { photos: [], pinGroups: [], notes: '' };
    const group = (current.pinGroups || []).find((g) => g.groupKey === groupKey);
    const pin = group?.pins?.[pinIndex];
    const pinKey = pin?.pinKey;
    const photosToDelete = pinKey
      ? (current.photos || []).filter((photo) => photo.id && photo.pinKey === pinKey)
      : [];
    setTaskWorkStep3((prev) => {
      const latest = prev[taskId] || { photos: [], pinGroups: [], notes: '' };
      const nextGroups = (latest.pinGroups || []).map((g) => {
        if (g.groupKey !== groupKey) return g;
        return { ...g, pins: (g.pins || []).filter((_, idx) => idx !== pinIndex) };
      });
      const nextPhotos = pinKey
        ? (latest.photos || []).filter((photo) => photo.pinKey !== pinKey)
        : (latest.photos || []);
      return { ...prev, [taskId]: { ...latest, pinGroups: nextGroups, photos: nextPhotos } };
    });
    if (projectId && photosToDelete.length > 0) {
      await Promise.all(
        photosToDelete.map((photo) =>
          deleteProjectTaskWorkInputPhoto(projectId, photo.id).catch((e) => console.error(e))
        )
      );
    }
  };

  const handleUploadTaskPhoto = async (taskId, pinKey, file, floorplanSnapshot = null) => {
    if (!projectId || !date || !file) return;
    const taskState = taskWorkStep3[taskId];
    const effectiveState = floorplanSnapshot ? { ...taskState, ...floorplanSnapshot } : taskState;
    const allGroups = effectiveState?.pinGroups || [];
    const allPins = allGroups.flatMap((group) => group.pins || []);
    if (!allPins.length) {
      alert('Save floorplan and pin first, then upload photos.');
      return;
    }
    const firstGroup = allGroups[0];
    const firstPin = (firstGroup?.pins || [])[0];
    try {
      // Ensure task pin payload is saved before attaching photos
      await upsertProjectTaskWorkInput(projectId, taskId, {
        date,
        floorplanObjectId: firstGroup.floorplanObjectId,
        floorplanFloorNumber: firstGroup.floorplanFloorNumber,
        floorplanPageNumber: 1,
        floorplanFileId: firstGroup.floorplanFileId,
        floorplanObjectKey: firstGroup.floorplanObjectKey,
        floorplanOriginalName: firstGroup.floorplanOriginalName,
        floorplanMimeType: firstGroup.floorplanMimeType,
        pinXPercent: firstPin.xPercent,
        pinYPercent: firstPin.yPercent,
        pins: allPins,
        pinGroups: allGroups,
        notes: effectiveState.notes || null,
      });
      const presign = await getProjectTaskWorkInputPhotoUploadUrl(projectId, taskId, {
        originalName: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      const { uploadUrl, objectKey } = presign.data;
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      const created = await createProjectTaskWorkInputPhoto(projectId, taskId, {
        date,
        objectKey,
        originalName: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size || 0,
        pinKey,
      });
      setTaskWorkStep3((prev) => {
        const current = prev[taskId] || { photos: [] };
        return {
          ...prev,
          [taskId]: {
            ...current,
            ...(floorplanSnapshot || {}),
            photos: [...(current.photos || []), created.data],
          },
        };
      });
    } catch (e) {
      console.error(e);
      alert('Photo upload failed. Please try again.');
    }
  };

  const handleDeleteTaskPhoto = async (taskId, photoId) => {
    if (!projectId) return;
    try {
      await deleteProjectTaskWorkInputPhoto(projectId, photoId);
      updateTaskStep3(taskId, {
        photos: (taskWorkStep3[taskId]?.photos || []).filter((p) => p.id !== photoId),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const submitAllSteps = async () => {
    if (!projectId || !date) return;
    setLoading(true);
    try {
      const taskIds = getUsedTasks.map((task) => task.id);
      const step3Saves = taskIds.map((taskId) => {
        const taskState = taskWorkStep3[taskId];
        const pinGroups = Array.isArray(taskState?.pinGroups) ? taskState.pinGroups : [];
        const groupsWithPins = pinGroups.filter((group) => Array.isArray(group.pins) && group.pins.length > 0);
        const firstGroup = groupsWithPins[0];
        const firstPin = firstGroup?.pins?.[0];
        const hasCompleteStep3Data = Boolean(
          taskState &&
          firstGroup &&
          firstPin
        );
        if (!hasCompleteStep3Data) {
          return upsertProjectTaskWorkInput(projectId, taskId, {
            date,
            clearPins: true,
            notes: taskState?.notes || null,
          });
        }
        return upsertProjectTaskWorkInput(projectId, taskId, {
          date,
          floorplanObjectId: firstGroup.floorplanObjectId,
          floorplanFloorNumber: firstGroup.floorplanFloorNumber,
          floorplanPageNumber: 1,
          floorplanFileId: firstGroup.floorplanFileId,
          floorplanObjectKey: firstGroup.floorplanObjectKey,
          floorplanOriginalName: firstGroup.floorplanOriginalName,
          floorplanMimeType: firstGroup.floorplanMimeType,
          pinXPercent: firstPin.xPercent,
          pinYPercent: firstPin.yPercent,
          pins: groupsWithPins.flatMap((group) => group.pins || []),
          pinGroups: groupsWithPins,
          notes: taskState.notes || null,
        });
      });

      if (step3Saves.length > 0) {
        await Promise.all(step3Saves);
      }

      // Refresh calendar completion dots immediately (no page refresh needed).
      try {
        await refreshCalendarMonthStatus();
      } catch (refreshError) {
        console.error('Error refreshing calendar status after step 3 save:', refreshError);
      }

      setActiveStep(0);
      setWorkers([]);
      setTaskWork({});
      await loadStep3Inputs();
      alert('Daily work input saved (steps 1-3).');
    } catch (e) {
      console.error(e);
      alert('Could not save step 3.');
    } finally {
      setLoading(false);
    }
  };

  const getUsedTasks = useMemo(() => {
    const usedTaskIds = new Set();
    existingLabor.forEach((le) => usedTaskIds.add(le.projectGeneralTaskId));
    Object.keys(taskWork).forEach((tid) => usedTaskIds.add(Number(tid)));
    existingWorkInputTaskIds.forEach((id) => usedTaskIds.add(Number(id)));
    return projectGeneralTasks.filter((t) => usedTaskIds.has(t.id));
  }, [existingLabor, taskWork, projectGeneralTasks, existingWorkInputTaskIds]);

  const usedGeneralTaskIds = useMemo(
    () => getUsedTasks.map((task) => task.generalTaskId).filter((id) => Number.isFinite(id)),
    [getUsedTasks]
  );

  const addProjectTaskToEditor = (projectTask) => {
    if (!projectTask?.id) return;
    setProjectGeneralTasks((prev) => (
      prev.some((task) => task.id === projectTask.id) ? prev : [...prev, projectTask]
    ));
    setTaskWork((prev) => {
      if (prev[projectTask.id]) return prev;
      return {
        ...prev,
        [projectTask.id]: { people: [], materials: [] },
      };
    });
    setAddTaskDialogOpen(false);
  };

  const handleAddExistingTask = async (generalTaskId) => {
    if (!projectId || addingCatalogTask) return;
    setAddingCatalogTask(true);
    try {
      let projectTask = projectGeneralTasks.find((task) => task.generalTaskId === generalTaskId);
      if (!projectTask) {
        const res = await ensureProjectGeneralTask(projectId, { generalTaskId });
        projectTask = res.data;
      }
      addProjectTaskToEditor(projectTask);
    } catch (e) {
      console.error(e);
      alert(t('dailyWorkInput.step2.couldNotAddTask', { defaultValue: 'Could not add general task to this date.' }));
    } finally {
      setAddingCatalogTask(false);
    }
  };

  const handleAddCategoryOnly = async (category) => {
    if (!projectId || addingCatalogTask) return;
    setAddingCatalogTask(true);
    try {
      const res = await ensureProjectGeneralTask(projectId, { category, categoryOnly: true });
      addProjectTaskToEditor(res.data);
      const catalogRes = await getGeneralTasks();
      setCatalogTasks(catalogRes.data || []);
    } catch (e) {
      console.error(e);
      alert(t('dailyWorkInput.step2.couldNotAddTask', { defaultValue: 'Could not add general task to this date.' }));
    } finally {
      setAddingCatalogTask(false);
    }
  };

  const handleAddNewCatalogTask = async ({ name, category }) => {
    if (!projectId || addingCatalogTask) return;
    setAddingCatalogTask(true);
    try {
      const res = await ensureProjectGeneralTask(projectId, { name, category });
      addProjectTaskToEditor(res.data);
      const catalogRes = await getGeneralTasks();
      setCatalogTasks(catalogRes.data || []);
    } catch (e) {
      console.error(e);
      alert(t('dailyWorkInput.step2.couldNotAddTask', { defaultValue: 'Could not add general task to this date.' }));
    } finally {
      setAddingCatalogTask(false);
    }
  };

  const previewSubcontractorOrderByWorkerKey = useMemo(() => {
    const maxExistingOrderByCompany = new Map();

    existingHours.forEach((eh) => {
      if (!eh.subcontractorCompanyId) return;
      const companyKey = String(eh.subcontractorCompanyId);
      const order = Number(eh.subcontractorOrder);
      const current = maxExistingOrderByCompany.get(companyKey) || 0;
      if (Number.isInteger(order) && order > current) {
        maxExistingOrderByCompany.set(companyKey, order);
      }
    });

    const nextOrderByCompany = new Map();
    maxExistingOrderByCompany.forEach((maxOrder, companyKey) => {
      nextOrderByCompany.set(companyKey, maxOrder + 1);
    });

    const byWorkerKey = new Map();
    workers.forEach((w) => {
      if (w.type !== 'subcontractor' || !w.companyId) return;
      const companyKey = String(w.companyId);
      const nextOrder = nextOrderByCompany.get(companyKey) || 1;
      byWorkerKey.set(w.key, nextOrder);
      nextOrderByCompany.set(companyKey, nextOrder + 1);
    });

    return byWorkerKey;
  }, [workers, existingHours]);

  // Get people/workers from Step 1 (workers and existing hours)
  const getStep1People = useMemo(() => {
    const step1People = [];
    
    // Add people from new workers in Step 1
    workers.forEach(w => {
      if (w.type === 'person' && w.personnelId) {
        const person = personnel.find(p => String(p.id) === String(w.personnelId));
        if (person) {
          step1People.push({
            slotKey: `person-${w.personnelId}`,
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            nickname: person.nickname,
            type: 'person'
          });
        }
      } else if (w.type === 'subcontractor' && w.companyId) {
        const company = companies.find(c => String(c.id) === String(w.companyId));
        if (company) {
          const previewOrder = previewSubcontractorOrderByWorkerKey.get(w.key) ?? null;
          step1People.push({
            slotKey: `subcontractor-${w.companyId}-${previewOrder != null ? previewOrder : ''}`,
            id: company.id,
            name: company.name,
            subcontractorOrder: previewOrder,
            type: 'subcontractor'
          });
        }
      }
    });
    
    // Add people from existing hours
    existingHours.forEach(eh => {
      if (eh.personnelId && eh.personnel) {
        step1People.push({
          slotKey: `person-${eh.personnelId}`,
          id: eh.personnel.id,
          firstName: eh.personnel.firstName,
          lastName: eh.personnel.lastName,
          nickname: eh.personnel.nickname,
          type: 'person'
        });
      } else if (eh.subcontractorCompanyId && eh.subcontractorCompany) {
        step1People.push({
          slotKey: `subcontractor-${eh.subcontractorCompanyId}-${eh.subcontractorOrder != null ? eh.subcontractorOrder : ''}`,
          id: eh.subcontractorCompany.id,
          name: eh.subcontractorCompany.name,
          subcontractorOrder: eh.subcontractorOrder != null ? eh.subcontractorOrder : null,
          type: 'subcontractor'
        });
      }
    });
    
    return step1People;
  }, [workers, existingHours, personnel, companies, previewSubcontractorOrderByWorkerKey]);

  // Get only personnel (not subcontractors) from Step 1
  const getStep1Personnel = useMemo(() => {
    return getStep1People.filter(p => p.type === 'person');
  }, [getStep1People]);

  // Get only subcontractors from Step 1
  const getStep1Subcontractors = useMemo(() => {
    return getStep1People.filter(p => p.type === 'subcontractor');
  }, [getStep1People]);

  // Check if any subcontractors were added in Step 1
  const hasSubcontractorsInStep1 = useMemo(() => {
    return getStep1Subcontractors.length > 0;
  }, [getStep1Subcontractors]);

  const duplicatePersonnelIdsInStep1 = useMemo(() => {
    const counts = new Map();
    workers.forEach((w) => {
      if (w.type !== 'person' || !w.personnelId) return;
      const key = String(w.personnelId);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return new Set(Array.from(counts.entries()).filter(([, count]) => count > 1).map(([id]) => id));
  }, [workers]);

  const existingPersonnelIdsForDate = useMemo(() => {
    const ids = new Set();
    existingHours.forEach((eh) => {
      if (eh.personnelId) ids.add(String(eh.personnelId));
    });
    return ids;
  }, [existingHours]);

  const step1TotalPeopleCount = useMemo(() => getStep1People.length, [getStep1People]);

  const step2PeopleCountByTaskId = useMemo(() => {
    const counts = new Map();

    existingLabor.forEach((le) => {
      if (getEffectiveLaborHoursWorked(le) <= 0) return;
      if (!le.personnelId && !le.subcontractorCompanyId) return;
      counts.set(le.projectGeneralTaskId, (counts.get(le.projectGeneralTaskId) || 0) + 1);
    });

    Object.entries(taskWork).forEach(([taskId, work]) => {
      const numericTaskId = Number(taskId);
      const newCount = (work.people || []).filter((p) => p.personnelId || p.companyId).length;
      counts.set(numericTaskId, (counts.get(numericTaskId) || 0) + newCount);
    });

    return counts;
  }, [existingLabor, taskWork, editingLaborManDays]);

  const allStep2AssignedWorkerKeys = useMemo(() => {
    const assigned = new Set();

    existingLabor.forEach((le) => {
      if (getEffectiveLaborHoursWorked(le) <= 0) return;
      if (le.personnelId) {
        assigned.add(`person-${le.personnelId}`);
      } else if (le.subcontractorCompanyId) {
        assigned.add(`subcontractor-${le.subcontractorCompanyId}-${le.subcontractorOrder != null ? le.subcontractorOrder : ''}`);
      }
    });

    Object.values(taskWork).forEach((work) => {
      (work.people || []).forEach((p) => {
        if (p.type === 'person' && p.personnelId) {
          assigned.add(`person-${p.personnelId}`);
        } else if (p.type === 'subcontractor' && p.companyId) {
          assigned.add(`subcontractor-${p.companyId}-${p.subcontractorOrder != null ? p.subcontractorOrder : ''}`);
        }
      });
    });

    return assigned;
  }, [existingLabor, taskWork, editingLaborManDays]);

  const missingStep2Assignments = useMemo(() => {
    return getStep1People.filter((p) => !allStep2AssignedWorkerKeys.has(p.slotKey));
  }, [getStep1People, allStep2AssignedWorkerKeys]);

  const canSubmitStep2 = useMemo(() => {
    if (loading) return false;
    if (!projectId || !date) return false;
    for (const count of step2PeopleCountByTaskId.values()) {
      if (count > step1TotalPeopleCount) return false;
    }
    return true;
  }, [loading, projectId, date, step2PeopleCountByTaskId, step1TotalPeopleCount]);

  const canSubmitStep3 = useMemo(() => {
    if (loading) return false;
    if (!projectId || !date) return false;
    return true;
  }, [loading, projectId, date]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{t('nav.dailyWorkInput')}</Typography>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Card>
          <CardContent>
            {/* Top row: project only */}
            <Box mb={3}>
              <FormControl fullWidth sx={{ maxWidth: 400 }}>
                <InputLabel id="project-label">{t('projectTimelines.selectProject')}</InputLabel>
                <Select
                  labelId="project-label"
                  id="project-select"
                  label={t('projectTimelines.selectProject')}
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  aria-label={t('projectTimelines.selectProject')}
                >
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Two columns: left = entries + actions, right = month calendar */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
                {existingHours.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom id="existing-entries-heading">{t('dailyWorkInput.existingEntriesForDate', { defaultValue: 'Existing entries for this date' })}</Typography>
                    <Box aria-labelledby="existing-entries-heading">
                      {existingHours.map((eh) => {
                        const draft = getExistingDraft(eh);
                        const preview = previewNetHours(draft, dailyWorkPrefs);
                        const isEdited = editingExisting[eh.id] != null;
                        return (
                          <Box key={eh.id} sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} md={3}>
                                <Typography>
                                  {eh.personnel ? formatPersonnelName(eh.personnel) : (eh.subcontractorCompany ? eh.subcontractorCompany.name : 'Subcontractor')}
                                  {!eh.personnelId && eh.subcontractorOrder != null ? ` (#${eh.subcontractorOrder})` : ''}
                                </Typography>
                              </Grid>
                              {isTimeRangeFormat ? (
                                <>
                                  <Grid item xs={6} md={2}>
                                    <TextField
                                      type="time"
                                      label={t('dailyWorkInput.startTime', { defaultValue: 'From' })}
                                      value={draft.startTime}
                                      onChange={(e) => patchExistingDraft(eh.id, { startTime: e.target.value })}
                                      fullWidth
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                    />
                                  </Grid>
                                  <Grid item xs={6} md={2}>
                                    <TextField
                                      type="time"
                                      label={t('dailyWorkInput.endTime', { defaultValue: 'Till' })}
                                      value={draft.endTime}
                                      onChange={(e) => patchExistingDraft(eh.id, { endTime: e.target.value })}
                                      fullWidth
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {preview ? preview.hours : eh.hours}h
                                    </Typography>
                                  </Grid>
                                </>
                              ) : (
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    type="number"
                                    label={t('personnel.fields.hours')}
                                    inputProps={{ step: 0.5, min: 0, 'aria-label': t('personnel.fields.hours') }}
                                    value={draft.hours}
                                    onChange={(e) => patchExistingDraft(eh.id, { hours: e.target.value })}
                                    fullWidth
                                  />
                                </Grid>
                              )}
                              <Grid item xs={6} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                  control={(
                                    <Checkbox
                                      checked={Boolean(draft.usedLunch)}
                                      onChange={(e) => patchExistingDraft(eh.id, { usedLunch: e.target.checked })}
                                      size="small"
                                    />
                                  )}
                                  label={t('dailyWorkInput.lunch', { defaultValue: 'Lunch' })}
                                  sx={{ m: 0 }}
                                />
                              </Grid>
                              <Grid item xs={6} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                                {isEdited && (
                                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                                    {t('dailyWorkInput.edited', { defaultValue: 'Edited' })}
                                  </Typography>
                                )}
                                {!isWorkerNoteVisible(`eh-${eh.id}`, draft.notes) && (
                                  <Tooltip title={t('dailyWorkInput.addNote', { defaultValue: 'Add note' })}>
                                    <IconButton
                                      size="medium"
                                      onClick={() => openWorkerNote(`eh-${eh.id}`)}
                                      aria-label={t('dailyWorkInput.addNote', { defaultValue: 'Add note' })}
                                    >
                                      <NoteAddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={t('common.delete')}>
                                  <IconButton
                                    size="medium"
                                    color="error"
                                    aria-label={t('common.delete')}
                                    onClick={async () => {
                                  await deletePersonnelHours(eh.id);
                                  const res = await getPersonnelHoursForProject(projectId, { from: date, to: date });
                                  setExistingHours(res.data || []);
                                  try {
                                    const laborRes = await getProjectLaborEntries(projectId);
                                    const laborEntries = (laborRes.data || []).filter(le =>
                                      toLocalDateString(le.date) === date &&
                                      (
                                        (eh.personnelId && le.personnelId === eh.personnelId) ||
                                        (
                                          !eh.personnelId &&
                                          le.subcontractorCompanyId === eh.subcontractorCompanyId &&
                                          Number(le.subcontractorOrder) === Number(eh.subcontractorOrder)
                                        )
                                      )
                                    );
                                    for (const entry of laborEntries) {
                                      await deleteProjectLaborEntry(projectId, entry.id);
                                    }
                                    const updatedLaborRes = await getProjectLaborEntries(projectId);
                                    setExistingLabor((updatedLaborRes.data || []).filter((x) => toLocalDateString(x.date) === date));
                                  } catch (e) {
                                    console.error('Error removing labor entries:', e);
                                  }
                                }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Grid>
                              {isWorkerNoteVisible(`eh-${eh.id}`, draft.notes) && (
                                <Grid item xs={12}>
                                  <TextField
                                    label={t('dailyWorkInput.workerNote', { defaultValue: 'Note' })}
                                    value={draft.notes}
                                    onChange={(e) => patchExistingDraft(eh.id, { notes: e.target.value })}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    autoFocus={Boolean(workerNoteOpen[`eh-${eh.id}`]) && !String(draft.notes || '').trim()}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                  py={1.5}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                    {isTimeRangeFormat && (
                      <>
                        <TextField
                          type="time"
                          label={t('dailyWorkInput.startTime', { defaultValue: 'From' })}
                          value={bulkStartTime}
                          onChange={(e) => setBulkStartTime(e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ width: 140 }}
                        />
                        <TextField
                          type="time"
                          label={t('dailyWorkInput.endTime', { defaultValue: 'Till' })}
                          value={bulkEndTime}
                          onChange={(e) => setBulkEndTime(e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          sx={{ width: 140 }}
                        />
                        <Button
                          onClick={handleBulkSetFrom}
                          variant="outlined"
                          disabled={!projectId || loading || !isValidTimeHhMm(bulkStartTime)}
                        >
                          {t('dailyWorkInput.setFrom', { defaultValue: 'Set from' })}
                        </Button>
                        <Button
                          onClick={handleBulkSetTo}
                          variant="outlined"
                          disabled={!projectId || loading || !isValidTimeHhMm(bulkEndTime)}
                        >
                          {t('dailyWorkInput.setTo', { defaultValue: 'Set to' })}
                        </Button>
                      </>
                    )}
                  </Box>
                  <Button
                    onClick={handleDuplicateRecentStep1}
                    variant="outlined"
                    disabled={!projectId || loading}
                    aria-label={t('dailyWorkInput.duplicateMostRecentInput', { defaultValue: 'Duplicate most recent input' })}
                  >
                    {t('dailyWorkInput.duplicateMostRecentInput', { defaultValue: 'Duplicate most recent input' })}
                  </Button>
                </Box>
                {workers.map((w) => {
                  const preview = previewNetHours(w, dailyWorkPrefs);
                  return (
                    <Box key={w.key} sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={2}>
                          {w.type === 'person' ? (
                            <FormControl fullWidth>
                              <InputLabel id={`pers-${w.key}`}>{t('dailyWorkInput.person', { defaultValue: 'Person' })}</InputLabel>
                              <Select labelId={`pers-${w.key}`} label={t('dailyWorkInput.person', { defaultValue: 'Person' })} value={w.personnelId} onChange={(e) => setWorkers(prev => prev.map(x => x.key === w.key ? { ...x, personnelId: e.target.value } : x))}>
                                {personnel.map((p) => (
                                  <MenuItem
                                    key={p.id}
                                    value={p.id}
                                    disabled={existingPersonnelIdsForDate.has(String(p.id)) || workers.some((other) => other.key !== w.key && other.type === 'person' && String(other.personnelId) === String(p.id))}
                                  >
                                    {formatPersonnelName(p)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <FormControl fullWidth>
                              <InputLabel id={`comp-${w.key}`}>{t('dailyWorkInput.subcontractorCompany', { defaultValue: 'Subcontractor company' })}</InputLabel>
                              <Select labelId={`comp-${w.key}`} label={t('dailyWorkInput.subcontractorCompany', { defaultValue: 'Subcontractor company' })} value={w.companyId} onChange={(e) => setWorkers(prev => prev.map(x => x.key === w.key ? { ...x, companyId: e.target.value } : x))}>
                                {companies.map((c) => (
                                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                        {w.type === 'subcontractor' ? (
                          <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              {w.companyId ? `#${previewSubcontractorOrderByWorkerKey.get(w.key) || 1}` : '-'}
                            </Typography>
                          </Grid>
                        ) : (
                          <Grid item xs={12} md={1} />
                        )}
                        {isTimeRangeFormat ? (
                          <>
                            <Grid item xs={6} md={2}>
                              <TextField
                                type="time"
                                label={t('dailyWorkInput.startTime', { defaultValue: 'From' })}
                                value={w.startTime || ''}
                                onChange={(e) => setWorkers(prev => prev.map(x => x.key === w.key ? { ...x, startTime: e.target.value } : x))}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                              />
                            </Grid>
                            <Grid item xs={6} md={2}>
                              <TextField
                                type="time"
                                label={t('dailyWorkInput.endTime', { defaultValue: 'Till' })}
                                value={w.endTime || ''}
                                onChange={(e) => setWorkers(prev => prev.map(x => x.key === w.key ? { ...x, endTime: e.target.value } : x))}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                              />
                            </Grid>
                            <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                {preview ? `${preview.hours}h` : '—'}
                              </Typography>
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={12} md={2}>
                            <TextField label={t('personnel.fields.hours')} type="number" inputProps={{ step: 0.5, min: 0 }} fullWidth value={w.hours} onChange={(e) => setWorkers(prev => prev.map(x => x.key === w.key ? { ...x, hours: e.target.value } : x))} />
                          </Grid>
                        )}
                        <Grid item xs={6} md={1}>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={Boolean(w.usedLunch)}
                                onChange={(e) => setWorkers(prev => prev.map(x => x.key === w.key ? { ...x, usedLunch: e.target.checked } : x))}
                                size="small"
                              />
                            )}
                            label={t('dailyWorkInput.lunch', { defaultValue: 'Lunch' })}
                            sx={{ m: 0 }}
                          />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          {!isWorkerNoteVisible(w.key, w.notes) && (
                            <Tooltip title={t('dailyWorkInput.addNote', { defaultValue: 'Add note' })}>
                              <IconButton
                                size="small"
                                onClick={() => openWorkerNote(w.key)}
                                aria-label={t('dailyWorkInput.addNote', { defaultValue: 'Add note' })}
                              >
                                <NoteAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t('common.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveWorker(w.key)}
                              aria-label={t('dailyWorkInput.removeWorker', { defaultValue: 'Remove worker' })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                        {isWorkerNoteVisible(w.key, w.notes) && (
                          <Grid item xs={12}>
                            <TextField
                              label={t('dailyWorkInput.workerNote', { defaultValue: 'Note' })}
                              value={w.notes || ''}
                              onChange={(e) => setWorkers(prev => prev.map(x => x.key === w.key ? { ...x, notes: e.target.value } : x))}
                              fullWidth
                              multiline
                              minRows={2}
                              autoFocus={Boolean(workerNoteOpen[w.key]) && !String(w.notes || '').trim()}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  );
                })}
                <Box display="flex" flexWrap="wrap" gap={1} mt={2} mb={1}>
                  <Button startIcon={<AddIcon />} onClick={handleAddPerson} variant="outlined" aria-label={t('dailyWorkInput.addPerson', { defaultValue: 'Add person' })}>{t('dailyWorkInput.addPerson', { defaultValue: 'Add person' })}</Button>
                  <Button startIcon={<AddIcon />} onClick={handleAddSubcontractor} variant="outlined" aria-label={t('dailyWorkInput.addSubcontractorWorker', { defaultValue: 'Add subcontractor worker' })}>{t('dailyWorkInput.addSubcontractorWorker', { defaultValue: 'Add subcontractor worker' })}</Button>
                </Box>
                <Box mt={2}>
                  <Typography variant="body2">{t('dailyWorkInput.totalHours', { defaultValue: 'Total hours' })}: {totalHours}</Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={wasRaining}
                          onChange={(e) => handleRainDayToggle(e.target.checked)}
                          color="primary"
                          inputProps={{ 'aria-label': t('dailyWorkInput.rainDay', { defaultValue: 'Rain day' }) }}
                          sx={{
                            '& .MuiSwitch-switchBase:not(.Mui-checked)': {
                              color: 'grey.300',
                            },
                            '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': {
                              backgroundColor: 'grey.500',
                              opacity: 1,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'action.hover' },
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                              opacity: 1,
                            },
                          }}
                        />
                      }
                      label={t('dailyWorkInput.rainDay', { defaultValue: 'Rain day' })}
                      labelPlacement="start"
                      sx={{ m: 0, gap: 1, '& .MuiFormControlLabel-label': { typography: 'body2' } }}
                    />
                  </Box>
                  {duplicatePersonnelIdsInStep1.size > 0 && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      {t('dailyWorkInput.duplicatePersonWarning', { defaultValue: 'Same person is selected multiple times. Each person can only be added once per date.' })}
                    </Typography>
                  )}
                </Box>
                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                  <Button disabled={!canProceedStep1 || loading} variant="contained" onClick={handleSaveStep1}>{t('dailyWorkInput.saveAndContinue', { defaultValue: 'Save and continue' })}</Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
                <DailyWorkMonthCalendar date={date} viewMonth={viewMonth} onSelectDate={handleSelectDate} onViewMonthChange={handleViewMonthChange} dateEntryStatus={dateEntryStatus} legendKey="dailyWorkInput.calendarDotsExplanation" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Dialog open={rainLunchDialogOpen} onClose={cancelRainDialog}>
        <DialogTitle>{t('dailyWorkInput.rainDay', { defaultValue: 'Rain day' })}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('dailyWorkInput.rainDisableLunchPrompt', {
              defaultValue: 'Disable lunch for all workers for this day?',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRainDialog}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={confirmRainKeepLunch}>
            {t('dailyWorkInput.rainKeepLunch', { defaultValue: 'Keep lunch' })}
          </Button>
          <Button onClick={confirmRainDisableLunch} variant="contained" color="primary">
            {t('dailyWorkInput.rainDisableLunch', { defaultValue: 'Disable lunch' })}
          </Button>
        </DialogActions>
      </Dialog>
      {activeStep === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('dailyWorkInput.step2.title', { defaultValue: 'Tasks — work assignment' })}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('dailyWorkInput.step2.subtitle', { defaultValue: 'Tasks are optional. Assigning people and materials is also optional.' })}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
                <Box mb={3} display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={() => setAddTaskDialogOpen(true)}
                    disabled={!projectId || addingCatalogTask}
                  >
                    {t('dailyWorkInput.step2.addTask', { defaultValue: 'Add task' })}
                  </Button>
                  <Button 
                    onClick={handleDuplicateRecentStep2} 
                    variant="outlined" 
                    disabled={!projectId || loading}
                  >
                    {t('dailyWorkInput.duplicateMostRecentInput', { defaultValue: 'Duplicate most recent input' })}
                  </Button>
                </Box>
            
            {getUsedTasks.map((task) => (
              <Box key={task.id} mb={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">
                        {formatDailyWorkTaskLabel(task, t('dailyWorkInput.step2.unspecifiedTask', { defaultValue: 'task unspecified' }))}
                      </Typography>
                      <Tooltip title={t('dailyWorkInput.step2.removeTaskFromEditor', { defaultValue: 'Remove this task from today’s editor' })}>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => removeTask(task.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TextField
                      label={t('dailyWorkInput.taskNote', { defaultValue: 'Task note (optional)' })}
                      value={taskNotes[task.id] || ''}
                      onChange={(e) => setTaskNotes((prev) => ({ ...prev, [task.id]: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={2}
                      sx={{ mb: 2 }}
                    />
                    
                    {/* People Section */}
                    <Box mb={3}>
                      <Typography variant="body2" gutterBottom>{t('dailyWorkInput.step2.people', { defaultValue: 'People' })}</Typography>
                      <Box display="flex" gap={1} mb={2} alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        {t('dailyWorkInput.step2.peopleOnTask', { defaultValue: 'People on task' })}: {step2PeopleCountByTaskId.get(task.id) || 0}/{step1TotalPeopleCount || 0}
                      </Typography>
                        {getStep1Personnel.length > 0 && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            startIcon={<AddIcon />}
                            onClick={() => addPersonToCostSheet(task.id)}
                            disabled={(step2PeopleCountByTaskId.get(task.id) || 0) >= step1TotalPeopleCount}
                          >
                            {t('dailyWorkInput.step2.addPerson', { defaultValue: 'Add Person' })}
                          </Button>
                        )}
                        {hasSubcontractorsInStep1 && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            startIcon={<AddIcon />}
                            onClick={() => addSubcontractorToCostSheet(task.id)}
                            disabled={(step2PeopleCountByTaskId.get(task.id) || 0) >= step1TotalPeopleCount}
                          >
                            {t('dailyWorkInput.step2.addSubcontractor', { defaultValue: 'Add Subcontractor' })}
                          </Button>
                        )}
                        {step1TotalPeopleCount > 0 && (step2PeopleCountByTaskId.get(task.id) || 0) >= step1TotalPeopleCount && (
                          <Typography variant="caption" color="warning.main">
                            {t('dailyWorkInput.step2.taskUsesAllWorkers', { defaultValue: 'This task already uses all workers from step 1.' })}
                          </Typography>
                        )}
                        {getStep1Personnel.length === 0 && !hasSubcontractorsInStep1 && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('dailyWorkInput.step2.noPeopleInStep1', { defaultValue: 'No people selected in step 1' })}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Existing people entries */}
                      {(() => {
                        const existingPeopleEntries = existingLabor.filter((le) =>
                          le.projectGeneralTaskId === task.id &&
                          (le.personnelId || le.subcontractorCompanyId)
                        );

                        return existingPeopleEntries.map((le) => (
                          <Grid key={le.id} container spacing={2} alignItems="center" sx={{ mb: 1, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2">
                                {le.personnel
                                  ? formatPersonnelName(le.personnel)
                                  : (le.subcontractorCompany?.name || 'Subcontractor')
                                }
                                {!le.personnelId && le.subcontractorOrder != null ? ` (#${le.subcontractorOrder})` : ''}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {t('dailyWorkInput.step2.existing', { defaultValue: '(existing)' })}
                                </Typography>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField 
                                label={t('dailyWorkInput.step2.manDays', { defaultValue: 'Man-days' })} 
                                type="number" 
                                inputProps={{ step: 0.5, min: 0.5, max: 1 }} 
                                fullWidth 
                                value={getLaborManDaysDisplay(le)}
                                onChange={(e) => {
                                  setEditingLaborManDays((prev) => ({ ...prev, [le.id]: e.target.value }));
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                              {editingLaborManDays[le.id] != null &&
                                String(editingLaborManDays[le.id]) !== String(le.hoursWorked / 8) && (
                                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                                    {t('dailyWorkInput.edited', { defaultValue: 'Edited' })}
                                  </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Tooltip title={t('common.delete')}>
                                <IconButton onClick={async () => {
                                  await deleteProjectLaborEntry(projectId, le.id);
                                  setEditingLaborManDays((prev) => {
                                    const next = { ...prev };
                                    delete next[le.id];
                                    return next;
                                  });
                                  const res = await getProjectLaborEntries(projectId);
                                  setExistingLabor((res.data || []).filter((x) => toLocalDateString(x.date) === date));
                                }}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        ));
                      })()}
                      
                      {/* New people entries */}
                      {(() => {
                        const existingPeopleEntries = existingLabor.filter((le) =>
                          le.projectGeneralTaskId === task.id &&
                          (le.personnelId || le.subcontractorCompanyId) &&
                          getEffectiveLaborHoursWorked(le) > 0
                        );
                        return (taskWork[task.id]?.people || []).map((person) => {
                          const takenPersonnelIds = new Set(
                            existingPeopleEntries
                              .filter((le) => le.personnelId)
                              .map((le) => String(le.personnelId))
                          );
                          const takenSubcontractorSlots = new Set(
                            existingPeopleEntries
                              .filter((le) => le.subcontractorCompanyId)
                              .map((le) => `${le.subcontractorCompanyId}|${le.subcontractorOrder != null ? le.subcontractorOrder : ''}`)
                          );

                          (taskWork[task.id]?.people || []).forEach((p) => {
                            if (p.key === person.key) return;
                            if (p.type === 'person' && p.personnelId) takenPersonnelIds.add(String(p.personnelId));
                            if (p.type === 'subcontractor' && p.companyId) {
                              takenSubcontractorSlots.add(`${p.companyId}|${p.subcontractorOrder != null ? p.subcontractorOrder : ''}`);
                            }
                          });

                          return (
                            <Grid key={person.key} container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                              <Grid item xs={12} md={4}>
                                {person.type === 'person' ? (
                                  <FormControl fullWidth>
                                    <InputLabel id={`person-${task.id}-${person.key}`}>{t('dailyWorkInput.person', { defaultValue: 'Person' })}</InputLabel>
                                    <Select
                                      labelId={`person-${task.id}-${person.key}`}
                                      label={t('dailyWorkInput.person', { defaultValue: 'Person' })}
                                      value={person.personnelId}
                                      onChange={(e) => updatePersonInCostSheet(task.id, person.key, { personnelId: e.target.value })}
                                    >
                                      {getStep1Personnel.map((p) => (
                                        <MenuItem key={`${p.slotKey}-${p.id}`} value={p.id} disabled={takenPersonnelIds.has(String(p.id))}>
                                          {formatPersonnelName(p)}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <FormControl fullWidth>
                                    <InputLabel id={`company-${task.id}-${person.key}`}>{t('dailyWorkInput.subcontractorCompany', { defaultValue: 'Subcontractor Company' })}</InputLabel>
                                    <Select
                                      labelId={`company-${task.id}-${person.key}`}
                                      label={t('dailyWorkInput.subcontractorCompany', { defaultValue: 'Subcontractor Company' })}
                                      value={person.companyId && person.subcontractorOrder != null ? `${person.companyId}|${person.subcontractorOrder}` : ''}
                                      onChange={(e) => {
                                        const [companyIdRaw, orderRaw] = String(e.target.value).split('|');
                                        updatePersonInCostSheet(task.id, person.key, {
                                          companyId: companyIdRaw || '',
                                          subcontractorOrder: orderRaw != null && orderRaw !== '' ? Number(orderRaw) : null
                                        });
                                      }}
                                    >
                                      {getStep1Subcontractors
                                        .filter((c) => c.subcontractorOrder != null)
                                        .map((c) => {
                                          const slot = `${c.id}|${c.subcontractorOrder != null ? c.subcontractorOrder : ''}`;
                                          return (
                                            <MenuItem key={`${c.slotKey}-${c.id}`} value={slot} disabled={takenSubcontractorSlots.has(slot)}>
                                              {c.name}{c.subcontractorOrder != null ? ` (#${c.subcontractorOrder})` : ''}
                                            </MenuItem>
                                          );
                                        })}
                                    </Select>
                                  </FormControl>
                                )}
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  label={t('dailyWorkInput.step2.manDays', { defaultValue: 'Man-days' })}
                                  type="number"
                                  inputProps={{ step: 0.5, min: 0.5, max: 1 }}
                                  fullWidth
                                  value={person.manDays}
                                  onChange={(e) =>
                                    updatePersonInCostSheet(task.id, person.key, {
                                      manDays: e.target.value === '' ? '' : parseFloat(e.target.value),
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={2}>
                                <Tooltip title={t('common.delete')}>
                                  <IconButton onClick={() => removePersonFromCostSheet(task.id, person.key)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Grid>
                            </Grid>
                          );
                        });
                      })()}
                    </Box>
                    
                    {/* Materials Section */}
                    <Box>
                      <Typography variant="body2" gutterBottom>{t('dailyWorkInput.step2.materialsEquipment', { defaultValue: 'Materials/Equipment' })}</Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        startIcon={<AddIcon />}
                        onClick={() => addMaterialToCostSheet(task.id)}
                        sx={{ mb: 2 }}
                      >
                        {t('dailyWorkInput.step2.addMaterialEquipment', { defaultValue: 'Add Material/Equipment' })}
                      </Button>
                      
                      {/* Existing material entries */}
                      {(() => {
                        const existingMaterialEntries = existingLabor.filter(le => 
                          le.projectGeneralTaskId === task.id && 
                          le.amount > 0 && 
                          (le.materialEquipmentId || le.subcontractorEquipmentName)
                        );
                        
                        return existingMaterialEntries.map((material) => (
                          <Grid key={material.id} container spacing={2} alignItems="center" sx={{ mb: 1, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                            <Grid item xs={12} md={5}>
                              <Typography variant="body2">
                                {material.materialEquipment?.name || 
                                 (material.subcontractorEquipmentName ? 
                                   `${material.subcontractorEquipmentName} (${material.subcontractorEquipmentType || t('dailyWorkInput.step2.equipment', { defaultValue: 'Equipment' })})` : 
                                   t('dailyWorkInput.step2.unknownMaterial', { defaultValue: 'Unknown Material' }))}
                                {material.subcontractorEquipmentUnit && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    [{material.subcontractorEquipmentUnit}]
                                  </Typography>
                                )}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  ({material.materialEquipmentId ? t('dailyWorkInput.step2.ourEquipment', { defaultValue: 'our equipment' }) : t('dailyWorkInput.step2.subcontractorEquipment', { defaultValue: 'subcontractor equipment' })})
                                </Typography>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField 
                                label={t('siteDelivery.amount')} 
                                type="number" 
                                inputProps={{ step: 0.01, min: 0 }} 
                                fullWidth 
                                value={material.amount} 
                                onChange={async (e) => {
                                  await updateProjectLaborEntry(projectId, material.id, { amount: Number(e.target.value) });
                                  const res = await getProjectLaborEntries(projectId);
                                  setExistingLabor((res.data || []).filter((x) => toLocalDateString(x.date) === date));
                                }} 
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Tooltip title={t('common.delete')}>
                                <IconButton onClick={async () => {
                                  await deleteProjectLaborEntry(projectId, material.id);
                                  const res = await getProjectLaborEntries(projectId);
                                  setExistingLabor((res.data || []).filter((x) => toLocalDateString(x.date) === date));
                                }}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        ));
                      })()}
                      
                      {/* New material entries */}
                      {(taskWork[task.id]?.materials || []).map((material) => (
                        <Box key={material.key} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          {/* Equipment Type Toggle */}
                          <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <Typography variant="body2" gutterBottom>{t('dailyWorkInput.step2.equipmentType', { defaultValue: 'Equipment Type' })}</Typography>
                            <Box display="flex" gap={2}>
                              <Button
                                variant={material.equipmentType === 'our' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => updateMaterialInCostSheet(task.id, material.key, { 
                                  equipmentType: 'our',
                                  materialEquipmentId: '',
                                  subcontractorEquipmentName: '',
                                  subcontractorEquipmentType: '',
                                  subcontractorEquipmentUnit: ''
                                })}
                              >
                                {t('dailyWorkInput.step2.ourEquipmentLabel', { defaultValue: 'Our Equipment' })}
                              </Button>
                              <Button
                                variant={material.equipmentType === 'subcontractor' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => updateMaterialInCostSheet(task.id, material.key, { 
                                  equipmentType: 'subcontractor',
                                  materialEquipmentId: '',
                                  subcontractorEquipmentName: '',
                                  subcontractorEquipmentType: '',
                                  subcontractorEquipmentUnit: ''
                                })}
                              >
                                {t('dailyWorkInput.step2.subcontractorEquipmentLabel', { defaultValue: 'Subcontractor Equipment' })}
                              </Button>
                            </Box>
                          </FormControl>

                          <Grid container spacing={2} alignItems="center">
                            {material.equipmentType === 'our' ? (
                              // Our Equipment - Show dropdown
                              <>
                                <Grid item xs={12} md={5}>
                                  <FormControl fullWidth>
                                    <InputLabel id={`material-${task.id}-${material.key}`}>Item</InputLabel>
                                    <Select 
                                      labelId={`material-${task.id}-${material.key}`} 
                                    label={t('dailyWorkInput.step2.item', { defaultValue: 'Item' })} 
                                      value={material.materialEquipmentId} 
                                      onChange={(e) => updateMaterialInCostSheet(task.id, material.key, { materialEquipmentId: e.target.value })}
                                      disabled={materials.length === 0}
                                    >
                                      {materials.map((me) => (
                                        <MenuItem key={me.id} value={String(me.id)}>{me.name}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  {materials.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {t('dailyWorkInput.step2.noMaterialsDelivered', {
                                        defaultValue: 'No materials have been delivered to this project yet.',
                                      })}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField 
                                    label={t('siteDelivery.amount')} 
                                    type="number" 
                                    inputProps={{ step: 0.01, min: 0 }} 
                                    fullWidth 
                                    value={material.amount} 
                                    onChange={(e) => updateMaterialInCostSheet(task.id, material.key, { amount: e.target.value })} 
                                  />
                                </Grid>
                              </>
                            ) : (
                              // Subcontractor Equipment - Show dropdowns and text field
                              <>
                                <Grid item xs={12} md={3}>
                                  <TextField 
                                    label={t('dailyWorkInput.step2.equipmentName', { defaultValue: 'Equipment Name' })} 
                                    fullWidth 
                                    value={material.subcontractorEquipmentName} 
                                    onChange={(e) => updateMaterialInCostSheet(task.id, material.key, { subcontractorEquipmentName: e.target.value })} 
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <FormControl fullWidth>
                                    <InputLabel id={`subcontractor-type-${task.id}-${material.key}`}>{t('tools.fields.type', { defaultValue: 'Type' })}</InputLabel>
                                    <Select 
                                      labelId={`subcontractor-type-${task.id}-${material.key}`} 
                                      label={t('tools.fields.type', { defaultValue: 'Type' })} 
                                      value={material.subcontractorEquipmentType} 
                                      onChange={(e) => updateMaterialInCostSheet(task.id, material.key, { subcontractorEquipmentType: e.target.value })}
                                    >
                                      {TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <FormControl fullWidth>
                                    <InputLabel id={`subcontractor-unit-${task.id}-${material.key}`}>{t('costSheetsPage.unit', { defaultValue: 'Unit' })}</InputLabel>
                                    <Select 
                                      labelId={`subcontractor-unit-${task.id}-${material.key}`} 
                                      label={t('costSheetsPage.unit', { defaultValue: 'Unit' })} 
                                      value={material.subcontractorEquipmentUnit} 
                                      onChange={(e) => updateMaterialInCostSheet(task.id, material.key, { subcontractorEquipmentUnit: e.target.value })}
                                    >
                                      {unitOptions.map((unit) => (
                                        <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField 
                                    label={t('siteDelivery.amount')} 
                                    type="number" 
                                    inputProps={{ step: 0.01, min: 0 }} 
                                    fullWidth 
                                    value={material.amount} 
                                    onChange={(e) => updateMaterialInCostSheet(task.id, material.key, { amount: e.target.value })} 
                                  />
                                </Grid>
                              </>
                            )}
                            <Grid item xs={12} md={2}>
                              <Tooltip title={t('common.delete')}>
                                <IconButton onClick={() => removeMaterialFromCostSheet(task.id, material.key)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
            
                {getUsedTasks.length === 0 && (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="text.secondary">
                      {t('dailyWorkInput.step2.noTasksInEditor', { defaultValue: 'No tasks in the editor. Click "Add task" or save to continue without tasks.' })}
                    </Typography>
                  </Box>
                )}

                {step1TotalPeopleCount > 0 && (
                  <Box sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('dailyWorkInput.step2.assignedWorkers', { defaultValue: 'Assigned workers' })}: {step1TotalPeopleCount - missingStep2Assignments.length}/{step1TotalPeopleCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {t('dailyWorkInput.step2.peopleOptionalHint', {
                        defaultValue: 'Assigning workers to tasks is optional.',
                      })}
                    </Typography>
                    {missingStep2Assignments.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('dailyWorkInput.step2.unassignedWorkers', { defaultValue: 'Not assigned to a task' })}: {missingStep2Assignments.map((p) => (
                          p.type === 'person'
                            ? formatPersonnelName(p)
                            : `${p.name}${p.subcontractorOrder != null ? ` (#${p.subcontractorOrder})` : ''}`
                        )).join(', ')}
                      </Typography>
                    )}
                  </Box>
                )}
            
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Button variant="outlined" onClick={() => setActiveStep(0)}>{t('common.back', { defaultValue: 'Back' })}</Button>
                  <Button variant="contained" disabled={!canSubmitStep2} onClick={saveStep2AndContinue}>{t('dailyWorkInput.saveAndContinue', { defaultValue: 'Save and continue' })}</Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
                <DailyWorkMonthCalendar date={date} viewMonth={viewMonth} onSelectDate={handleSelectDate} onViewMonthChange={handleViewMonthChange} dateEntryStatus={dateEntryStatus} legendKey="dailyWorkInput.calendarDotsExplanation" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeStep === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('dailyWorkInput.step3.title', { defaultValue: 'Task location pin and photos' })}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('dailyWorkInput.step3.subtitle', { defaultValue: 'Pins and photos are optional. For each task you can select object and floor, put a pin on the floorplan, then upload work photos.' })}
            </Typography>
            {getUsedTasks.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  {t('dailyWorkInput.step3.noTasksInEditor', { defaultValue: 'No tasks for this date. You can submit without pins or photos.' })}
                </Typography>
              </Box>
            )}
            {getUsedTasks.map((task) => {
              const state = taskWorkStep3[task.id] || { photos: [], pinGroups: [], notes: '' };
              return (
                <Card key={task.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {formatDailyWorkTaskLabel(task, t('dailyWorkInput.step2.unspecifiedTask', { defaultValue: 'task unspecified' }))}
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => addPinGroup(task.id)}>
                        {t('dailyWorkInput.step3.addFloorplan', { defaultValue: 'Add floorplan' })}
                      </Button>
                    </Box>
                    {(state.pinGroups || []).map((group, groupIndex) => {
                      const selectedObject = floorplanObjects.find((obj) => obj.id === Number(group.floorplanObjectId));
                      const floorCount = Math.max(1, Number(selectedObject?.floorCount || 1));
                      const selectedFile = (selectedObject?.files || []).find((file) => file.floorNumber === Number(group.floorplanFloorNumber));
                      const floorLabel = selectedObject?.floorNames?.[String(group.floorplanFloorNumber)] || (group.floorplanFloorNumber ? `Floor ${group.floorplanFloorNumber}` : 'Floor');
                      return (
                        <Card key={group.groupKey} variant="outlined" sx={{ mb: 1 }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">{t('dailyWorkInput.step3.floorplanN', { defaultValue: 'Floorplan' })} #{groupIndex + 1}</Typography>
                              <Button size="small" color="error" onClick={() => removePinGroup(task.id, group.groupKey)}>{t('common.delete')}</Button>
                            </Box>
                            <Grid container spacing={2} sx={{ mb: 1 }}>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                  <InputLabel id={`step3-object-${task.id}-${group.groupKey}`}>{t('dailyWorkInput.step3.object', { defaultValue: 'Object' })}</InputLabel>
                                  <Select
                                    labelId={`step3-object-${task.id}-${group.groupKey}`}
                                    label={t('dailyWorkInput.step3.object', { defaultValue: 'Object' })}
                                    value={group.floorplanObjectId || ''}
                                    onChange={(e) => {
                                      const objectId = Number(e.target.value);
                                      handleSelectTaskFloorplan(task.id, group.groupKey, objectId, 1);
                                    }}
                                  >
                                    {floorplanObjects.map((obj) => <MenuItem key={obj.id} value={obj.id}>{obj.objectName}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <FormControl fullWidth disabled={!group.floorplanObjectId}>
                                  <InputLabel id={`step3-floor-${task.id}-${group.groupKey}`}>{t('dailyWorkInput.step3.floor', { defaultValue: 'Floor' })}</InputLabel>
                                  <Select
                                    labelId={`step3-floor-${task.id}-${group.groupKey}`}
                                    label={t('dailyWorkInput.step3.floor', { defaultValue: 'Floor' })}
                                    value={group.floorplanFloorNumber || ''}
                                    onChange={(e) => {
                                      handleSelectTaskFloorplan(task.id, group.groupKey, Number(group.floorplanObjectId), Number(e.target.value));
                                    }}
                                  >
                                    {Array.from({ length: floorCount }, (_, idx) => idx + 1).map((floorNo) => (
                                      <MenuItem key={floorNo} value={floorNo}>
                                        {selectedObject?.floorNames?.[String(floorNo)] || `Floor ${floorNo}`}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                            {group.floorplanFloorNumber && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                {t('dailyWorkInput.step3.selectedFloor', { defaultValue: 'Selected floor' })}: {floorLabel}
                              </Typography>
                            )}
                            {selectedFile?.id ? (
                              <FloorplanPinPicker
                                projectId={projectId}
                                fileId={selectedFile.id}
                                mimeType={selectedFile.mimeType}
                                pins={group.pins || []}
                                onPinsChange={(pins) => {
                                  setTaskWorkStep3((prev) => {
                                    const current = prev[task.id] || { photos: [], pinGroups: [], notes: '' };
                                    const nextGroups = (current.pinGroups || []).map((g) => (
                                      g.groupKey === group.groupKey
                                        ? {
                                          ...g,
                                          floorplanFileId: selectedFile.id,
                                          floorplanObjectKey: selectedFile.objectKey,
                                          floorplanOriginalName: selectedFile.originalName,
                                          floorplanMimeType: selectedFile.mimeType,
                                          pins: pins.map((pin) => ({ ...pin, pinKey: pin.pinKey || generateUUID() })),
                                        }
                                        : g
                                    ));
                                    return { ...prev, [task.id]: { ...current, pinGroups: nextGroups } };
                                  });
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                                {t('dailyWorkInput.step3.noFloorplanFile', { defaultValue: 'Selected floor has no floorplan file. Upload it in Project Details → Floorplans.' })}
                              </Typography>
                            )}
                            {(group.pins || []).length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">{t('dailyWorkInput.step3.pins', { defaultValue: 'Pins' })}: {group.pins.length}</Typography>
                                {(group.pins || []).map((pin, pinIndex) => (
                                  <Box key={pin.pinKey || `${group.groupKey}-${pinIndex}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.5, flexWrap: 'wrap' }}>
                                    <PinNumberBadge pinIndex={pinIndex} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" component="div">
                                      {t('dailyWorkInput.step3.pinN', { defaultValue: 'Pin' })} {pinIndex + 1}: {pin.xPercent.toFixed(1)}%, {pin.yPercent.toFixed(1)}%
                                    </Typography>
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => removePin(task.id, group.groupKey, pinIndex)}
                                    >
                                      {t('dailyWorkInput.step3.deletePin', { defaultValue: 'Delete pin' })}
                                    </Button>
                                    <Button variant="outlined" size="small" component="label" disabled={!selectedFile?.id}>
                                      {t('dailyWorkInput.step3.uploadPictures', { defaultValue: 'Upload picture(s)' })}
                                      <input
                                        hidden
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={async (e) => {
                                          const files = Array.from(e.target.files || []);
                                          for (const file of files) {
                                            await handleUploadTaskPhoto(task.id, pin.pinKey, file);
                                          }
                                          e.target.value = '';
                                        }}
                                      />
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                      {(state.photos || []).filter((photo) => photo.pinKey === pin.pinKey).length} {t('dailyWorkInput.step3.photosCount', { defaultValue: 'photo(s)' })}
                                    </Typography>
                                    {(state.photos || []).filter((photo) => photo.pinKey === pin.pinKey).length > 0 && (
                                      <Box sx={{ width: '100%', mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                          {t('dailyWorkInput.step3.photosForPin', { defaultValue: 'Photos for this pin' })}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                          {(state.photos || [])
                                            .filter((photo) => photo.pinKey === pin.pinKey)
                                            .map((photo) => (
                                              <Box
                                                key={photo.id}
                                                sx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  overflow: 'hidden',
                                                  width: 96,
                                                }}
                                              >
                                                <Box
                                                  component="a"
                                                  href={photo.url}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  sx={{ display: 'block', lineHeight: 0 }}
                                                >
                                                  <Box
                                                    component="img"
                                                    src={photo.url}
                                                    alt={photo.originalName}
                                                    sx={{ width: 96, height: 72, objectFit: 'cover', display: 'block' }}
                                                  />
                                                </Box>
                                                <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                  <Typography variant="caption" noWrap title={photo.originalName}>
                                                    {photo.originalName}
                                                  </Typography>
                                                  <Button size="small" color="error" sx={{ alignSelf: 'flex-start', minWidth: 0, py: 0 }}
                                                    onClick={() => handleDeleteTaskPhoto(task.id, photo.id)}
                                                  >
                                                    {t('common.delete')}
                                                  </Button>
                                                </Box>
                                              </Box>
                                            ))}
                                        </Box>
                                      </Box>
                                    )}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                    {(state.photos || []).some((photo) => !photo.pinKey) && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {t('dailyWorkInput.step3.unassignedPhotos', { defaultValue: 'Photos without a pin' })}
                        </Typography>
                        {(state.photos || []).filter((photo) => !photo.pinKey).map((photo) => (
                          <Box key={photo.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                            <Button size="small" href={photo.url} target="_blank" rel="noreferrer">
                              {photo.originalName}
                            </Button>
                            <Button size="small" color="error" onClick={() => handleDeleteTaskPhoto(task.id, photo.id)}>{t('common.delete')}</Button>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button variant="outlined" onClick={handleBackToStep2}>{t('common.back', { defaultValue: 'Back' })}</Button>
              <Button variant="contained" disabled={!canSubmitStep3} onClick={submitAllSteps}>{t('dailyWorkInput.submitDailyWork', { defaultValue: 'Submit daily work' })}</Button>
            </Box>
          </CardContent>
        </Card>
      )}
      
      <AddTaskDialog
        open={addTaskDialogOpen}
        onClose={() => setAddTaskDialogOpen(false)}
        catalogTasks={catalogTasks}
        usedGeneralTaskIds={usedGeneralTaskIds}
        adding={addingCatalogTask}
        onAddExisting={handleAddExistingTask}
        onAddCategoryOnly={handleAddCategoryOnly}
        onAddNewTask={handleAddNewCatalogTask}
      />

      {/* Duplicate Recent Input Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={() => {
        setDuplicateDialogOpen(false);
        setDuplicateStep(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>{t('dailyWorkInput.duplicateMostRecentInput', { defaultValue: 'Duplicate most recent input' })}</DialogTitle>
        <DialogContent>
          {recentEntries && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Found recent entries from {formatDateEuropean(recentEntries.date)}. 
                {duplicateStep === 'step1' 
                  ? t('dailyWorkInput.duplicateStep1Hint', { defaultValue: ' This will populate Step 1 with the same people (workers) from the most recent input.' })
                  : t('dailyWorkInput.duplicateStep2Hint', { defaultValue: ' This will populate Step 2 with the same task assignments and materials from the most recent input.' })
                }
              </Typography>
              
              {duplicateStep === 'step1' && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    {t('dailyWorkInput.peopleToBeAddedStep1', { defaultValue: 'People that will be added to Step 1:' })}
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                    {recentEntries.entries
                      .filter((entry) => {
                        const hours = Number(entry.hours);
                        return Number.isFinite(hours) && hours > 0 && (entry.personnelId || entry.subcontractorCompanyId);
                      })
                      .map((entry, index) => {
                        const name = entry.personnelId
                          ? formatPersonnelName(entry.personnel)
                          : `${entry.subcontractorCompany?.name || 'Subcontractor'}${entry.subcontractorOrder != null ? ` (#${entry.subcontractorOrder})` : ''}`;
                        return (
                          <Typography key={index} variant="body2">
                            • {name}: {entry.hours} hours
                          </Typography>
                        );
                      })}
                  </Box>
                </>
              )}
              
              {duplicateStep === 'step2' && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    {t('dailyWorkInput.tasksPopulatedStep2', { defaultValue: 'Tasks that will be populated in Step 2:' })}
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {(() => {
                      const taskMap = new Map();
                      recentEntries.entries.forEach((entry) => {
                        if (entry.hoursWorked > 0 || entry.amount) {
                          const tid = entry.projectGeneralTaskId;
                          const name = entry.projectGeneralTask?.name || `Task #${tid}`;
                          if (!taskMap.has(tid)) {
                            taskMap.set(tid, { name, count: 1 });
                          } else {
                            taskMap.get(tid).count += 1;
                          }
                        }
                      });

                      return Array.from(taskMap.values()).map((row, index) => (
                        <Typography key={index} variant="body2">
                          • {row.name} ({row.count} entries)
                        </Typography>
                      ));
                    })()}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDuplicateDialogOpen(false);
            setDuplicateStep(null);
          }}>{t('common.cancel')}</Button>
          <Button 
            onClick={duplicateStep === 'step1' ? handleApplyDuplicateStep1 : handleApplyDuplicateStep2} 
            variant="contained"
          >
            {t('dailyWorkInput.applyDuplicate', { defaultValue: 'Apply Duplicate' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DailyWorkInput;


